
/* eslint-disable import/first */
import React, {
  useEffect,
  useRef,
  useState,
  useContext,
  forwardRef,
  useImperativeHandle,
} from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Modal, Tooltip, DatePicker } from "antd";
import { ErrorMessage, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import TextError from "../../../TextError";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect, { components as reactSelectComponents } from "react-select";
import { httpinstance } from "../../../../axios/api";
import {
  getallairports,
  getOriginAirports,
  searchratestate,
  getOptionalVas,
  getQuery,
} from "../../../../store/SearchRates/SearchRatesSlice";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import minMax from "dayjs/plugin/minMax";
import { useLocation, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
  vehicletype,
  caldistance,
} from "../../../../store/vehicletype/Vehicletypeslice";
import FailureSearchRate from "../../../../components/SearchRates/FailureSearchRate";
import { ManageSocketContext } from "../../../../ManageSocketContext";
import { getsinglebookingdata } from "../../../../store/BookingHistory/BookingHistorySlice";
import {
  updateStoredSearch,
  updatequeryid,
  updateWeightMode,
} from "../../../../store/Newsearchform/Newsearchform";
import { setAwbNumberStore, clearAwbNumberStore } from "../../../../store/Newslices/awbSlice";
import Autocomplete from "react-google-autocomplete";
import { IncoTerms } from "../../../../utils/dropdownValues";
import { MdFlightTakeoff, MdCategory, MdInventory2, MdPayment } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AutofillUnitsDrawer from "../PackagingDetails/AutofillUnitsDrawer";
import { authuser } from "../../../../store/Authentication/AuthSlice";
import { isQueryLimitReached } from "../../../../utils/queryLimit";
import VerifyEmailModal from "../../../../components/common/VerifyEmailModal";

// Enables dayjs(str, "DD/MM/YYYY") parsing used by the departure-date field.
dayjs.extend(customParseFormat);
dayjs.extend(minMax);

// ─── Styles ───────────────────────────────────────────────────────────────────
const cardStyle = {
  background: "#fff",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #f0e8d5",
};

const fieldLabelStyle = {
  display: "block",
  fontSize: 10,
  fontWeight: 600,
  color: "#a8a29e",
  letterSpacing: ".08em",
  textTransform: "uppercase",
  marginBottom: 4,
};

const reactSelectStyles = {
  control: (base, state) => ({
    ...base,
    background: "#fafaf9",
    borderColor: state.isFocused ? "#ffaf00" : "#e7e0d6",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(255,175,0,.12)" : "none",
    borderRadius: 7,
    height: 32,
    minHeight: 32,
    padding: "0 6px",
    "&:hover": { borderColor: "#ffaf00" },
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "2px 10px", margin: 0}),
    input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
    indicatorsContainer: (base) => ({
    ...base,
    height: 30,
  }),

  singleValue: (base) => ({ ...base, color: "#292524", fontSize: 13.5, margin: 0 }),
  placeholder: (base) => ({ ...base, color: "#c4b8a8", fontSize: 13.5, margin: 0 }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected ? "#ffaf00" : state.isFocused ? "#fffbf0" : "#fff",
    color: state.isSelected ? "#fff" : "#292524",
    fontSize: 13.5,
    "&:active": { background: "#ffe082" },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 9,
    border: "1px solid #ede8e0",
    zIndex: 9999,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.selectProps.menuIsOpen ? "#ffaf00" : "#a8a29e",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
  }),
};

// Preferred-airline multi-select: same look as the Commodity field, with
// amber-tinted tags for the selected carriers.
const airlineSelectStyles = {
  ...reactSelectStyles,
  // Multi-select can hold several carrier tags, so unlike the single-line base
  // control we let the control grow vertically and wrap the tags instead of
  // clipping them to a fixed 32px height (which caused the chips to overflow).
  control: (base, state) => ({
    ...reactSelectStyles.control(base, state),
    height: "auto",
    minHeight: 34,
  }),
  // Inset the option rows so the highlighted/selected rows have a little space
  // from the edges and rounded corners on left and right.
  menuList: (base) => ({ ...base, padding: "6px 0" }),
  option: (base, state) => ({
    ...reactSelectStyles.option(base, state),
    margin: "2px 8px",
    width: "auto",
    borderRadius: 8,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "4px 8px",
    gap: 6,
    height: "auto",
    flexWrap: "wrap",
  }),
  multiValue: (base) => ({
    ...base,
    background: "#fffbf0",
    border: "1px solid #f0e8d5",
    borderRadius: 8,
    margin: "2px 4px",
    overflow: "hidden",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#292524",
    fontSize: 13,
    borderRadius: "8px 0 0 8px",
    paddingLeft: 8,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#a8a29e",
    borderRadius: "0 8px 8px 0",
    paddingRight: 6,
    ":hover": { background: "#f7efdc", color: "#292524" },
  }),
};

// ─── Multi-select "checkbox + overflow chip" behaviour (used by OPTIONAL VAS) ──
// Show at most this many chips inline; the rest collapse into a single "+N"
// chip whose tooltip lists the hidden selections.
const VAS_MAX_VISIBLE_CHIPS = 2;

// Option row with a leading checkbox so the dropdown reads as a multi-check list.
// The checkbox is presentational — react-select's own Option click still drives
// selection — so the input is read-only (onChange no-op).
const CheckboxOption = (props) => (
  <reactSelectComponents.Option {...props}>
    <input
      type="checkbox"
      checked={props.isSelected}
      onChange={() => null}
      style={{ marginRight: 8, accentColor: "#ffaf00", cursor: "pointer" }}
    />
    <span>{props.label}</span>
  </reactSelectComponents.Option>
);

// Render the first N selected values as normal chips; collapse everything after
// into a single "+X" chip that reveals the hidden labels on hover.
const OverflowMultiValue = (props) => {
  const { index, getValue } = props;
  const values = getValue();
  if (index < VAS_MAX_VISIBLE_CHIPS) {
    return <reactSelectComponents.MultiValue {...props} />;
  }
  if (index === VAS_MAX_VISIBLE_CHIPS) {
    const hidden = values.slice(VAS_MAX_VISIBLE_CHIPS);
    return (
      <Tooltip title={hidden.map((v) => v.label).join(", ")}>
        <div
          style={{
            background: "#fffbf0",
            border: "1px solid #f0e8d5",
            borderRadius: 8,
            margin: "2px 4px",
            padding: "0 8px",
            fontSize: 13,
            lineHeight: "22px",
            color: "#292524",
            cursor: "default",
            whiteSpace: "nowrap",
          }}
        >
          +{hidden.length}
        </div>
      </Tooltip>
    );
  }
  // Every value beyond the "+X" chip is already accounted for in the tooltip.
  return null;
};

// ─── Section header ───────────────────────────────────────────────────────────
function SectionHeader({ title, icon: Icon, style: extraStyle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 5, ...extraStyle }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 9,
          background: "#ffaf00",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 8px rgba(255,175,0,0.30)",
        }}
      >
        {Icon && <Icon size={18} color="#fff" />}
      </div>
      <span style={{ fontSize: 15, fontWeight: 700, color: "#292524", letterSpacing: ".01em" }}>{title}</span>
    </div>
  );
}

// ─── Commodity option formatter ───────────────────────────────────────────────
const formatCommodityOptionLabel = (option) => {
  const isPerishable =
    option?.commodityGroup === "Perishables" ||
    option?.commodityGroup === "Perishables No Temp";
  if (isPerishable) {
    return (
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            display: "inline-flex",
            color: "#854F0B",
            fontSize: 10,
            fontWeight: 500,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
          </svg>
        </span>
        {option.commodity_description}
      </span>
    );
  }
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          display: "inline-flex",
          color: "#57534e",
          flexShrink: 0,
        }}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.73L13 2.27a2 2 0 0 0-2 0L4 6.27A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
      </span>
      {option.commodity_description}
    </span>
  );
};

// ─── Preferred-airline option formatter ───────────────────────────────────────
// Used by ReactSelect for both the menu list and the selected tags.
const formatAirlineOptionLabel = (option) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
    {option.logoLocation ? (
      <img
        src={option.logoLocation}
        alt=""
        style={{ width: 20, height: 20, objectFit: "contain", flexShrink: 0 }}
      />
    ) : (
      <span
        style={{
          width: 20,
          height: 20,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#c4b8a8",
        }}
      >
        <MdFlightTakeoff size={16} />
      </span>
    )}
    <span>{option.label}</span>
  </span>
);

const roundDim = (v) => (v - Math.floor(v) > 0.5 ? Math.ceil(v) : Math.floor(v));

// ─── Perishables temperature-range options ────────────────────────────────────
// Shared by the toggle buttons below AND the editdata-prefill effect, so a
// saved query that only stored the range code (e.g. "2-8", no explicit
// upper/lower bounds) can still derive the numeric bounds the pharma payload
// needs instead of sending them through as null.
const TEMPERATURE_RANGES = [
  { label: "-10°C", value: "-10", lower: -10, upper: -30 },
  { label: "2-8°C", value: "2-8", lower: 2, upper: 8 },
  { label: "15-25°C", value: "15-25", lower: 15, upper: 25 },
  { label: "2-25°C", value: "2-25", lower: 2, upper: 25 },
];

// ─── Formik error flattening ──────────────────────────────────────────────────
// Yup nests errors following the schema shape; this walks that tree and returns
// the leaf message strings in traversal order so the first one can be surfaced.
const flattenFormikErrors = (errors) => {
  const messages = [];
  const walk = (node) => {
    if (!node) return;
    if (typeof node === "string") {
      messages.push(node);
    } else if (Array.isArray(node)) {
      node.forEach(walk);
    } else if (typeof node === "object") {
      Object.values(node).forEach(walk);
    }
  };
  walk(errors);
  return messages;
};

// ─── Row totals calculator ────────────────────────────────────────────────────
const calcRowTotals = (pkg, weightMode) => {
  const units = parseFloat(pkg.units) || 0;
  const l = parseFloat(pkg.length) || 0;
  const w = parseFloat(pkg.width) || 0;
  const h = parseFloat(pkg.height) || 0;
  const wt = parseFloat(pkg.weight) || 0;
  const cbm = (units * l * w * h) / 1000000;
  const totalWt = weightMode === 2 ? wt : units * wt;
  const volWt = (units * l * h * w) / 6000;

  // ── IN (inches) volume & vol-weight — separate from existing CM logic ────
  // CFT  = L × B × H × units / 1728  (standard cubic-feet formula)
  // CBM  from CFT: 1 CFT = 0.0283168 CBM
  // Vol weight for IN: convert each dim to cm (× 2.54) then apply the same
  //   /6000 divisor — equivalent to multiplying the raw IN vol-wt by 2.54³.
  // All three fields are null when unit is CM so existing code is unaffected.
  let cft = null;
  let cbmFromInch = null;
  let volWtInch = null;
  if (pkg.unit === "IN") {
    const rawCft = (units * l * w * h) / 1728;
    cft = isNaN(rawCft) ? 0 : parseFloat(rawCft.toFixed(6));
    cbmFromInch = isNaN(rawCft) ? 0 : parseFloat((rawCft * 0.0283168).toFixed(6));
    // Convert each inch dim to cm rounded to nearest integer — same rounding
    // used in the CM-conversion hint display — then apply standard /6000 formula.
    // Using the full decimal (e.g. 114.3) over-counts by ~0.8%; integer rounding
    // matches the reference system result (e.g. 45 in → 114 cm → 2469.3 kg).
    const lCm = roundDim(l * 2.54);
    const wCm = roundDim(w * 2.54);
    const hCm = roundDim(h * 2.54);
    const rawVolWtInch = (units * lCm * wCm * hCm) / 6000;
    volWtInch = isNaN(rawVolWtInch) ? 0 : parseFloat(rawVolWtInch.toFixed(2));
  }
  // ─────────────────────────────────────────────────────────────────────────

  return {
    totalsubunits: units,
    totalsubweight: isNaN(totalWt) ? 0 : parseFloat(totalWt.toFixed(2)),
    totalsubvolume: isNaN(cbm) ? 0 : parseFloat(cbm.toFixed(6)),
    // ── IN mode: use inch-aware vol weight; CM mode unchanged ───────────────
    volWeight: pkg.unit === "IN"
      ? (volWtInch ?? 0)
      : (isNaN(volWt) ? 0 : parseFloat(volWt.toFixed(2))),
    // ────────────────────────────────────────────────────────────────────────
    // IN-specific: null when unit is CM, populated when unit is IN
    cft,
    cbmFromInch,
  };
};

// ─── Sync stored totals to the package rows before a search ──────────────────
// The rows and the stored totals are prefilled from different sources
// (booking edit → Edit Query: rows from single_booking_data, totals from the
// last search), so they can disagree, e.g. a booked 11-unit row next to 12-unit
// totals. The form displays totals derived from the rows, so the rows are the
// truth: recompute the stored totals with the exact recalcAllTotals formula.
// When they already agree this writes back identical values (no payload change).
const syncTotalsFromPackages = (values) => {
  const pd = values?.packagingDetails;
  const pkgs = pd?.packagingType?.packages;
  if (!Array.isArray(pkgs) || pkgs.length === 0) return;
  if (pd?.packagingDetailsBy && pd.packagingDetailsBy !== "Packaging Type") return;

  const mode = pkgs[0]?.weightunit === "KG" ? 2 : 1;
  const totals = pkgs.map((p) => calcRowTotals(p, mode));
  const tUnits = totals.reduce((s, r) => s + r.totalsubunits, 0);
  // Incomplete rows — leave the stored totals untouched.
  if (!(tUnits > 0)) return;
  const tWeight = totals.reduce((s, r) => s + r.totalsubweight, 0);
  const tVolume = totals.reduce((s, r) => s + r.totalsubvolume, 0);
  const isInchMode = pkgs[0]?.unit === "IN";
  const tVolumeFinal = isInchMode
    ? totals.reduce((s, r) => s + (r.cft || 0), 0)
    : tVolume;
  const tVolWeight = parseFloat(
    totals.reduce((s, r) => s + r.volWeight, 0).toFixed(2)
  );

  pd.packagingType = {
    ...pd.packagingType,
    totalunits: tUnits,
    totalweight: tWeight,
    totalvolume: tVolumeFinal,
  };
  pd.shipmentTotal = {
    ...pd.shipmentTotal,
    totalunits: tUnits,
    totalweight: tWeight,
    totalvolume: tVolumeFinal,
    totalvolweight: tVolWeight,
    unit: isInchMode ? "CFT" : "CBM",
  };
};

const NewsearchForm = forwardRef(({ searchBy, searchFormData, type }, ref) => {
  const location = useLocation();
  const [anchorEl] = useState(null);
  const { hasnodata, setReceivingResponse } =
    useContext(ManageSocketContext);
  const loginedUser = useSelector(authuser);
  // /user-details user (loaded by PageLayout) — drives the unverified-user
  // query-count gate in findflights.
  const userDetails = useSelector((s) => s.data?.data?.data?.user);
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  const roleallowed=loginedUser?.userroles||""
  // When the logged-in user is a KAM, the entire search form is read-only.
  const isKam = roleallowed === "kam";
  // ── Queries "eye" (View) vs "Edit" actions ──────────────────────────────────
  // Queries.jsx opens this form in two modes via the `type` prop:
  //   • View (eye) → type="kam_admin"      → every field read-only, no search btn
  //   • Edit       → type="kam_admin_edit" → every field editable
  // A fresh search (no `type`) is unaffected and stays fully editable.
  const isViewOnly = type === "kam_admin";
  // pagetype=QUERIES (set by the Queries page "Edit" action) means every field
  // is editable — it overrides BOTH the role/view lock AND the queryId deep-link
  // lock below. Read straight off the URL so it's available here, ahead of the
  // useSearchParams-derived queryId further down. Any other origin is unchanged.
  const urlParamsForLock = new URLSearchParams(location?.search || "");
  const isQueriesEdit = urlParamsForLock.get("pagetype") === "QUERIES";
  // A field is locked for KAM users (existing behaviour) OR in view-only mode —
  // unless this is a Queries-page edit, which unlocks everything.
  const fieldsDisabled = isQueriesEdit ? false : (isKam || isViewOnly);
  // Origin/destination/preferred-airline are ALSO locked whenever a queryId is
  // present (deep-link edit of a booking); a Queries-page edit unlocks them too.
  const queryIdLock = isQueriesEdit ? false : !!urlParamsForLock.get("queryId");
  // console.log("userdata of serach qurey",searchFormData.searchquery.userDetails)
  const [formikData, setFormikData] = useState(null);
  const formikRef = useRef(null);
  const hasFetchedOriginAirports = useRef(false);

  useImperativeHandle(ref, () => ({
    submitForm: () => formikRef.current?.submitForm(),
  }));

  const backdata = useSelector(updateStoredSearch);
  const newsearchformbackdata = backdata?.payload?.newsearchform?.newsearchformdata;
  const state = useSelector((s) => s?.newqueries?.newqueriesdata);

  const [airports, setAirports] = useState([]);
  const [originAirports, setOriginAirports] = useState([]);
  const [, setWebOpen] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [popup, setPopup] = useState("");
  const [origin, setOrigin] = useState("");
  const [originvehicle, setOriginVehicle] = useState([]);
  const [destinationvehicle, setDestinationVehicle] = useState([]);
  const [destination, setDestination] = useState("");
  const [searchquery, setSearchQuery] = useState({});
  const [selectedVehicle, setSelectedVehicle] = useState({
    destinationVehicle: "",
    originVehicle: "",
  });
  const [originDistance, setOriginDistance] = useState();
  const [editdata, setEditData] = useState({});
  const [editbookeditem, setEditbookeditem] = useState();
  const [, setEditPayload] = useState({});
  const [destinationDistance, setDestinationDistance] = useState();
  const [, setVasOptionData] = useState([]);
  const editQueryData = location?.state;

  const [comodityMaster, setcomodityMaster] = useState([]);
  const [airlineOptions, setAirlineOptions] = useState([]);

  const [productDetails, setProductDetails] = useState({
    dgShipment: false,
    natureOfGoods: "General Cargo",
    commodityGroup: "General Cargo",
    commodityId: 115,
    commodityIdDisplay: 115,
    hscode: "",
    specialHandlingCodes: [],
  });

  const [commodityid, setCommodityid] = useState(115);
  const [temperaturecontrol, setTemperatureControl] = useState({
    tempraturerange:
      newsearchformbackdata?.temperatureRange ||
      searchFormData?.searchquery?.temperatureRange ||
      "",
    upperbound:
      newsearchformbackdata?.uppertempbound ||
      searchFormData?.searchquery?.upperBound ||
      "",
    lowerbound:
      newsearchformbackdata?.lowertempbound ||
      searchFormData?.searchquery?.lowerBound ||
      "",
  });
  const [, setSelectedDate] = useState(
    newsearchformbackdata?.searchquery?.goodsDetails?.cargoreadydate || ""
  );
  const [selectedcomondity, setSelectedComondity] = useState("");

  // Pickup/location states
  const [originPickup, setOriginPickup] = useState(
    newsearchformbackdata?.searchquery?.originAirport?.originPickup ||
    state?.originPort?.originPickup ||
    false
  );
  const [destPickup, setDestPickup] = useState(
    newsearchformbackdata?.searchquery?.destinationAirport?.destinationPickup ||
    state?.destinationPort?.destinationPickup ||
    false
  );
  const [originLocation, setOriginLocation] = useState(
    newsearchformbackdata?.searchquery?.originAirport?.originLocation ||
    state?.originPort?.originLocation ||
    ""
  );
  const [originLocationMarker, setOriginLocationMarker] = useState(
    newsearchformbackdata?.searchquery?.originAirport?.originLocationMarker ||
    state?.originPort?.originLocationMarker ||
    {}
  );
  const [originLocationCountry, setOriginLocationCountry] = useState(
    newsearchformbackdata?.searchquery?.originAirport?.originLocationCountry ||
    state?.originPort?.originLocationCountry ||
    ""
  );
  const [destinationLocation, setDestinationLocation] = useState(
    newsearchformbackdata?.searchquery?.destinationAirport?.destinationLocation ||
    state?.destinationPort?.destinationLocation ||
    ""
  );
  const [destinationLocationMarker, setDestinationLocationMarker] = useState(
    newsearchformbackdata?.searchquery?.destinationAirport?.destinationLocationMarker ||
    state?.destinationPort?.destinationLocationMarker ||
    {}
  );
  const [destinationLocationCountry, setDestinationLocationCountry] = useState(
    newsearchformbackdata?.searchquery?.destinationAirport?.destinationLocationCountry ||
    state?.destinationPort?.destinationLocationCountry ||
    ""
  );

  // IncoTerms & VAS
  const [incoTermsOpts, setIncoTermsOpts] = useState(IncoTerms);
  const [vasOptions, setVasOptions] = useState([]);
  const [vasLoading, setVasLoading] = useState(false);
  const [autofillDrawerOpen, setAutofillDrawerOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get("queryId");

  const allAirportsState = useSelector(searchratestate);

  // Reset origin location when originPickup turns off
  useEffect(() => {
    if (!originPickup) {
      setOriginLocation("");
      setOriginLocationMarker({});
      setOriginLocationCountry("");
    }
  }, [originPickup]);

  // Reset destination location when destPickup turns off
  useEffect(() => {
    if (!destPickup) {
      setDestinationLocationCountry("");
      setDestinationLocation("");
      setDestinationLocationMarker({});
    }
  }, [destPickup]);

  useEffect(() => {
    if (!queryId) return;
    // A query opened via the Queries "Edit" action carries its Quote_status in
    // router state. For still-quote-stage queries (Quote Pending / Auto Quoted)
    // we fetch the raw query via ratesearch/get-query instead of
    // single_booking_data.
    const editStatus = editQueryData?.Quote_status
      ?.toLowerCase()
      ?.trim()
      ?.replace(/\s+/g, "");
    if (editStatus === "quotepending" || editStatus === "autoquoted") {
      dispatch(getQuery({ queryId }))
        .then((res) => {
          const doc = res?.payload?.data?.data;
          if (doc) {
            // get-query returns the raw Query document with its fields at the
            // top level (originPort / destinationPort / goodsDetails /
            // packagingDetails / paymentTerms / preferred_airline / ...) — the
            // same shape the form already reads via `editdata.searchquery.*`.
            // Wrapping it as { searchquery: doc } lets Formik (enableReinitialize)
            // prefill every field, and fires the editdata-driven effects
            // (pickup, temperature, commodity lookup) just like the booking path.
            // NOTE: we intentionally do NOT set editbookeditem here — that drives
            // the booking-UPDATE submit path, whereas a quote-stage edit
            // (Auto Quoted / Quote Pending) is a fresh re-search, not a booking.
            setEditData({ searchquery: doc });
          } else {
            // Nothing came back for this id — fall back to the summary snapshot
            // passed via router state so the form isn't left blank.
            setEditData(editQueryData ? { searchquery: editQueryData } : {});
          }
        })
        .catch(() => { });
      return;
    }
    dispatch(getsinglebookingdata({ queryId }))
      .then((res) => {
        if (res?.payload?.data?.data) {
          setEditbookeditem(res?.payload?.data?.data);
          setEditPayload(
            res?.payload?.data?.data?.search_query_data || editQueryData?.search_query_data
          );
          setEditData(
            res?.payload?.data?.data || editQueryData?.search_query_data
          );
        } else {
          // No saved query came back for this id (empty / errored response) —
          // fall back to any snapshot passed via router state instead of
          // wiping the form with undefined.
          setEditPayload(editQueryData?.search_query_data);
          setEditData(editQueryData?.search_query_data);
        }
      })
      .catch(() => { });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const date = editdata?.searchquery?.goodsDetails?.cargoreadydate;
    if (date) setSelectedDate(date);
  }, [editdata]);

  useEffect(() => {
    const ep = editdata?.searchquery;
    if (!ep) return;
    const oPickup = !!ep.originPort?.originPickup;
    const dPickup = !!ep.destinationPort?.destinationPickup;
    setOriginPickup(oPickup);
    if (oPickup) {
      setOriginLocation(ep.originPort?.originLocation || "");
      setOriginLocationMarker(ep.originPort?.originLocationMarker || {});
      setOriginLocationCountry(ep.originPort?.originLocationCountry || "");
    }
    setDestPickup(dPickup);
    if (dPickup) {
      setDestinationLocation(ep.destinationPort?.destinationLocation || "");
      setDestinationLocationMarker(ep.destinationPort?.destinationLocationMarker || {});
      setDestinationLocationCountry(ep.destinationPort?.destinationLocationCountry || "");
    }
    const wm = ep.packagingDetails?.packagingType?.weightMode;
    if (wm) dispatch(updateWeightMode(wm));
  }, [editdata, dispatch]);

  // Sync weightMode to Redux on mount for newsearchformbackdata / state paths
  useEffect(() => {
    const wm =
      newsearchformbackdata?.searchquery?.packagingDetails?.packagingType?.weightMode ||
      state?.packagingDetails?.packagingType?.weightMode;
    if (wm) dispatch(updateWeightMode(wm));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (anchorEl && formikRef.current) {
      setFormikData(formikRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editdata]);

  useEffect(() => {
    const range =
      editdata?.temperatureRange ||
      editdata?.searchquery?.temperatureRange ||
      newsearchformbackdata?.temperatureRange;
    // Some saved queries only stored the range code, not explicit numeric
    // bounds — fall back to the same TEMPERATURE_RANGES lookup initialValues
    // uses, so the outgoing search payload (built from this state, not from
    // Formik) doesn't send null temperature bounds inside pharmaDtls.
    const rangeBounds = TEMPERATURE_RANGES.find((r) => r.value === range);
    const upper =
      editdata?.upperBound ||
      editdata?.searchquery?.uppertempbound ||
      newsearchformbackdata?.uppertempbound ||
      rangeBounds?.upper;
    const lower =
      editdata?.lowerBound ||
      editdata?.searchquery?.lowertempbound ||
      newsearchformbackdata?.lowertempbound ||
      rangeBounds?.lower;
    if (range || upper || lower) {
      setTemperatureControl({
        tempraturerange: range || "",
        upperbound: upper || "",
        lowerbound: lower || "",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editdata, editQueryData]);

  const fetchCommodityList = async () => {
    try {
      const response = await httpinstance.get("commodity-list");
      const comodityMasterOptions = response?.data?.data?.data?.map((item) => ({
        value: item?.id,
        commodityId: item?.id,
        label: item?.commodityDescription,
        commodity_description: item?.commodityDescription,
        commodityGroup: item?.commodityGroup,
        hscode: item?.hscode,
      }));
      setcomodityMaster(comodityMasterOptions ?? []);
    } catch (err) {
      // silently handle
    }
  };

  useEffect(() => {
    fetchCommodityList();
  }, []);

  // ─── Allowed (preferred) airline list ──────────────────────────────────────
  const fetchAllowedAirlines = async () => {
    try {
      const response = await httpinstance.get("allowed_airline_list/35");
      const list = response?.data?.data?.data || [];
      // `id` is now the allowed-airline ROW id; the airline's own id (what
      // preferred_airline stores and what saved queries are matched against)
      // is `airline_id`. Fall back to `id` only for an older backend.
      // Only carriers whose Rate Search is enabled in Carrier Management
      // (auto_search: 0 / null = enabled, 1 = disabled).
      const options = list
        .filter((item) => item && (item.airline_id ?? item.id))
        .filter((item) => Number(item.auto_search) !== 1)
        .map((item) => ({
          value: item.airline_id ?? item.id,
          label: item.carr_name,
          carr_name: item.carr_name,
          logoLocation: item.logoLocation,
        }));
      setAirlineOptions(options);
    } catch (err) {
      // silently handle
    }
  };

  useEffect(() => {
    fetchAllowedAirlines();
  }, []);

  const handlebackcomodity = async (commodityIntId) => {
    try {
      const response = await httpinstance.get(`commodity-details/${commodityIntId}`);
      const data = response?.data?.data?.data;
      if (!data) return;

      const commodityDesc = data?.commodityDescription ?? data?.commodity_description ?? "";
      const commodityGroup = data?.commodityGroup ?? "General Cargo";

      const productdetail1 = {
        dgShipment: false,
        natureOfGoods: commodityDesc,
        commodityGroup: commodityGroup,
        commodityId: data?.id,
        commodityIdDisplay: data?.id,
        hscode: data?.hscode || "",
        specialHandlingCodes: [],
      };

      setProductDetails(productdetail1);
      setCommodityid(data?.id);
      setSelectedComondity(data?.id);

      if (formikData) {
        formikData.setFieldValue("commodityId", data?.id);
        formikData.setFieldValue("commodityValue", data?.id);
        formikData.setFieldValue("commodity_description", commodityDesc);
        formikData.setFieldValue("commodityGroup", commodityGroup);
        formikData.setFieldValue("commodityCode", data?.hscode || "");
      }
    } catch (error) {
      // silently handle
    }
  };

  useEffect(() => {
    if (newsearchformbackdata?.commodityId) {
      handlebackcomodity(newsearchformbackdata?.commodityId);
    } else if (editdata?.searchquery?.goodsDetails?.commodityId) {
      handlebackcomodity(editdata?.searchquery?.goodsDetails?.commodityId);
    } else if (searchFormData?.searchquery?.goodsDetails?.commodityId) {
      // View (eye) / Auto-Quoted detail path: the query is passed straight in
      // via the searchFormData prop with NO `?queryId=` URL param, so `editdata`
      // never populates and neither branch above fires. Load the commodity from
      // the record's goodsDetails so the dropdown shows it instead of the empty
      // "Select commodity..." placeholder.
      handlebackcomodity(searchFormData?.searchquery?.goodsDetails?.commodityId);
    } else if (state?.goodsDetails?.commodityId) {
      // Same as above but for the redux-fed path (Queries dispatches
      // newqueries(record) before rendering the form).
      handlebackcomodity(state?.goodsDetails?.commodityId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsearchformbackdata, searchFormData, editdata, state]);

  const comoditydatabasedonqr_code = (option) => {
    const productdetail1 = {
      dgShipment: false,
      natureOfGoods: option?.commodity_description,
      commodityGroup: option?.commodityGroup,
      commodityId: option?.commodityId,
      commodityIdDisplay: option?.value,
      hscode: option?.hscode || "",
      specialHandlingCodes: [],
    };
    setProductDetails(productdetail1);
    setCommodityid(option?.value);

    // Only "Perishables" carries a temperature range — everything else
    // (including "Perishables No Temp") must not keep a stale one.
    if (option?.commodityGroup !== "Perishables") {
      setTemperatureControl({
        tempraturerange: "",
        lowerbound: "",
        upperbound: "",
      });
    }
  };

  const handlemodalclose = () => {
    setIsOpen(false);
  };


  // ─── Airport change handlers ──────────────────────────────────────────────
  const updateIncoTermsOptions = (originCountry, destCountry) => {
    if (!originCountry || !destCountry) return;
    if (originCountry === "INDIA" && destCountry !== "INDIA") {
      setIncoTermsOpts([
        {
          label: "Exports",
          options: [
            { value: "DDP", label: "DDP" },
            { value: "DAP", label: "DAP" },
            { value: "DAT", label: "DAT" },
            { value: "CPT", label: "CPT" },
            { value: "CIP", label: "CIP" },
            { value: "CIF", label: "CIF" },
            { value: "CFR", label: "CFR" },
          ],
        },
      ]);
    } else if (originCountry !== "INDIA" && destCountry === "INDIA") {
      setIncoTermsOpts([
        {
          label: "Imports",
          options: [
            { value: "FOB", label: "FOB" },
            { value: "EXW", label: "EXW" },
            { value: "FCA", label: "FCA" },
            { value: "FAS", label: "FAS" },
          ],
        },
      ]);
    } else if (originCountry !== "INDIA" && destCountry !== "INDIA") {
      setIncoTermsOpts([{ value: "Third Country", label: "Third Country" }]);
    }
  };

  const handleOriginChange = (option, formik) => {
    if (!option) {
      formik.setFieldValue("originAirport.originAirport", "");
      formik.setFieldValue("originAirport.origincityName", "");
      formik.setFieldValue("originAirport.origincountryName", "");
      formik.setFieldValue("originAirport.originLogoUrl", "");
      formik.setFieldValue("originAirport.originPortMarker", {});
      formik.setFieldValue("cass", "");
      return;
    }
    const destAirport = formik.values.destinationAirport.destinationAirport;
    const allAirports = Array.isArray(allAirportsState?.allairports) ? allAirportsState.allairports : [];
    const destData = allAirports.find(
      (a) => `${a.cityName} - (${a.code})` === destAirport
    );
    if (destAirport && destAirport === option.value) {
      Modal.error({ title: "Origin and Destination airports cannot be the same!" });
      return;
    }
    if (destData && option.origincountryName === "INDIA" && destData.countryName === "INDIA") {
      Modal.error({ title: "Local shipment is not allowed!" });
      return;
    }
    formik.setFieldValue("originAirport.originAirport", option.value);
    formik.setFieldValue("originAirport.origincityName", option.origincityName);
    formik.setFieldValue("originAirport.origincountryName", option.origincountryName);
    formik.setFieldValue("originAirport.originLogoUrl", option.country_logo_url ?? "");
    formik.setFieldValue("cass", option.iataCass);
    formik.setFieldValue("originAirport.originPortMarker", {
      lat: option.lat,
      lng: option.lon,
    });
    // NOTE: Auto-defaulting the incoterm from the origin/destination country was
    // removed on request — picking an origin/destination no longer pre-selects a
    // value in INCOTERMS. Incoterms is now a mandatory, explicit user choice, so
    // this block is intentionally commented out (updateIncoTermsOptions below
    // still narrows the AVAILABLE options to the route; it does not select one).
    // if (destData && !formik.values.paymentTerms.incoTerms) {
    //   if (option.origincountryName === "INDIA" && destData.countryName !== "INDIA") {
    //     formik.setFieldValue("paymentTerms.incoTerms", "CFR");
    //   } else if (option.origincountryName !== "INDIA" && destData.countryName === "INDIA") {
    //     formik.setFieldValue("paymentTerms.incoTerms", "FOB");
    //   } else if (option.origincountryName !== "INDIA" && destData.countryName !== "INDIA") {
    //     formik.setFieldValue("paymentTerms.incoTerms", "Third Country");
    //   }
    // }
    updateIncoTermsOptions(option.origincountryName, destData?.countryName);
  };

  const handleDestinationChange = (option, formik) => {
    if (!option) {
      formik.setFieldValue("destinationAirport.destinationAirport", "");
      formik.setFieldValue("destinationAirport.destinationcityName", "");
      formik.setFieldValue("destinationAirport.destinationcountryName", "");
      formik.setFieldValue("destinationAirport.destinationLogoUrl", "");
      formik.setFieldValue("destinationAirport.destinationPortMarker", {});
      return;
    }
    const originAirport = formik.values.originAirport.originAirport;
    const originCountry = formik.values.originAirport.origincountryName;
    if (originAirport && originAirport === option.value) {
      Modal.error({ title: "Origin and Destination airports cannot be the same!" });
      return;
    }
    if (originCountry === "INDIA" && option.origincountryName === "INDIA") {
      Modal.error({ title: "Local shipment is not allowed!" });
      return;
    }
    formik.setFieldValue("destinationAirport.destinationAirport", option.value);
    formik.setFieldValue("destinationAirport.destinationcityName", option.origincityName);
    formik.setFieldValue("destinationAirport.destinationcountryName", option.origincountryName);
    formik.setFieldValue("destinationAirport.destinationLogoUrl", option.country_logo_url ?? "");
    formik.setFieldValue("destinationAirport.destinationPortMarker", {
      lat: option.lat,
      lng: option.lon,
    });
    // NOTE: Auto-defaulting the incoterm from the origin/destination country was
    // removed on request — picking an origin/destination no longer pre-selects a
    // value in INCOTERMS. Incoterms is now a mandatory, explicit user choice, so
    // this block is intentionally commented out (updateIncoTermsOptions below
    // still narrows the AVAILABLE options to the route; it does not select one).
    // if (originCountry && !formik.values.paymentTerms.incoTerms) {
    //   if (originCountry === "INDIA" && option.origincountryName !== "INDIA") {
    //     formik.setFieldValue("paymentTerms.incoTerms", "CFR");
    //   } else if (originCountry !== "INDIA" && option.origincountryName === "INDIA") {
    //     formik.setFieldValue("paymentTerms.incoTerms", "FOB");
    //   } else if (originCountry !== "INDIA" && option.origincountryName !== "INDIA") {
    //     formik.setFieldValue("paymentTerms.incoTerms", "Third Country");
    //   }
    // }
    updateIncoTermsOptions(originCountry, option.origincountryName);
  };

  // ─── VAS fetch ────────────────────────────────────────────────────────────
  const getoptionalvas = (values) => {
    const origin = values?.originAirport?.originAirport;
    const destination = values?.destinationAirport?.destinationAirport;
    const incoterms = values?.paymentTerms?.incoTerms;
    // The endpoint keys VAS off route + incoterms, so it can't return anything
    // useful until origin, destination and incoterms are all set. Bail early
    // (and clear stale options) instead of firing a guaranteed-empty request.
    if (!origin || !destination || !incoterms) {
      setVasOptions([]);
      return;
    }
    const payload = {
      origin,
      destination,
      weight: values?.packagingDetails?.shipmentTotal?.totalweight,
      incoterms,
    };
    setVasLoading(true);
    dispatch(getOptionalVas(payload))
      .then((res) => {
        setVasOptions(res?.payload?.data?.data || []);
      })
      .catch(() => {
        setVasOptions([]);
      })
      .finally(() => {
        setVasLoading(false);
      });
  };

  // Edit / update / view / back-from-results: the query comes prefilled with a
  // saved VAS selection (paymentTerms.optionalVas — an array of vatIds). The VAS
  // <ReactSelect> can only render those chips once the VAS master list is loaded
  // (it resolves each saved vatId → label from `vasOptions`). Until now nothing
  // fetched that list except opening the dropdown, so a previously-chosen VAS
  // showed as empty on return. Eagerly fetch it whenever a prefilled query has a
  // saved selection. Resolve origin/destination/incoterms/weight straight from
  // the prefill sources (same precedence as initialValues) rather than from
  // Formik, so it isn't affected by enableReinitialize timing.
  useEffect(() => {
    const origin =
      newsearchformbackdata?.searchquery?.originAirport?.originAirport ||
      editdata?.searchquery?.originPort?.originAirport ||
      searchFormData?.searchquery?.originPort?.originAirport ||
      state?.originPort?.originAirport;
    const destination =
      newsearchformbackdata?.searchquery?.destinationAirport?.destinationAirport ||
      editdata?.searchquery?.destinationPort?.destinationAirport ||
      searchFormData?.searchquery?.destinationPort?.destinationAirport ||
      state?.destinationPort?.destinationAirport;
    const incoterms =
      newsearchformbackdata?.searchquery?.paymentTerms?.incoTerms ||
      editdata?.searchquery?.paymentTerms?.incoTerms ||
      searchFormData?.searchquery?.paymentTerms?.incoTerms ||
      state?.paymentTerms?.incoTerms;
    const weight =
      newsearchformbackdata?.searchquery?.packagingDetails?.shipmentTotal?.totalweight ||
      editdata?.searchquery?.packagingDetails?.shipmentTotal?.totalweight ||
      searchFormData?.searchquery?.packagingDetails?.shipmentTotal?.totalweight ||
      state?.packagingDetails?.shipmentTotal?.totalweight;
    const savedVas =
      newsearchformbackdata?.searchquery?.paymentTerms?.optionalVas ||
      editdata?.searchquery?.paymentTerms?.optionalVas ||
      searchFormData?.searchquery?.paymentTerms?.optionalVas ||
      state?.paymentTerms?.optionalVas;
    if (
      origin &&
      destination &&
      incoterms &&
      Array.isArray(savedVas) &&
      savedVas.length
    ) {
      getoptionalvas({
        originAirport: { originAirport: origin },
        destinationAirport: { destinationAirport: destination },
        paymentTerms: { incoTerms: incoterms },
        packagingDetails: { shipmentTotal: { totalweight: weight } },
      });
    }
    // `searchFormData.searchquery` (not the wrapper object, which callers
    // rebuild each render) is the stable ref that changes when the KAM edit
    // path backfills the full query document.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editdata, newsearchformbackdata, state, searchFormData?.searchquery]);

  const processPackages = (items) =>
    items.map((item) => {
      const h = item.handling || "Tiltable and Stackable";
      const turnableBool = item.turnable !== undefined ? item.turnable : (h.includes("Tiltable") && !h.includes("Non-Tiltable"));
      const stackableBool = item.stackable !== undefined ? item.stackable : (h.includes("Stackable") && !h.includes("Non-stackable"));
      return {
        units: item.units,
        length: item.length,
        width: item.width,
        height: item.height,
        unit: item.unit,
        weight: item.weight,
        weightunit: item.weightunit,
        packagetype: item.packagetype,
        handling: (turnableBool ? "Tiltable" : "Non-Tiltable") + " and " + (stackableBool ? "Stackable" : "Non-stackable"),
        turnable: turnableBool,
        stackable: stackableBool,
        totalsubunits: item.totalsubunits,
        totalsubweight: item.totalsubweight,
        totalsubvolume: item.totalsubvolume,
      };
    });

  let PackagesInit = [];
  if (searchFormData?.searchquery?.packagingDetails?.packagingType?.packages?.length > 0) {
    PackagesInit = processPackages(searchFormData.searchquery.packagingDetails.packagingType.packages);
  } else if (editdata?.packagingDetails?.packagingType?.packages?.length > 0) {
    PackagesInit = processPackages(editdata.packagingDetails.packagingType.packages);
  } else {
    PackagesInit.push({
      units: "",
      length: "",
      width: "",
      height: "",
      unit: "",
      weight: "",
      weightunit: "",
      packagetype: "Box",
      handling: "Non-Tiltable and Stackable",
      turnable: false,
      stackable: true,
      totalsubunits: "",
      totalsubweight: "",
      totalsubvolume: "",
      Temp: "",
    });
  }

  // ── Pre-compute vol weight using the same package resolution as initialValues ──
  // initialValues.packages checks: editdata.searchquery → newsearchformbackdata → PackagesInit
  // We must use the same chain here so that "Edit Query" (which repopulates from
  // newsearchformbackdata) and "back from booking" produce the correct non-zero value.
  // PackagesInit alone can be empty (default empty strings) on those re-entry paths.
  const _resolvedPkgsForVW = (() => {
    if (editdata?.searchquery?.packagingDetails?.packagingType?.packages?.length > 0)
      return processPackages(editdata.searchquery.packagingDetails.packagingType.packages);
    if (newsearchformbackdata?.searchquery?.packagingDetails?.packagingType?.packages?.length > 0)
      return processPackages(newsearchformbackdata.searchquery.packagingDetails.packagingType.packages);
    return PackagesInit;
  })();
  const _initPkgMode = _resolvedPkgsForVW[0]?.weightunit === "KG" ? 2 : 1;
  const initTotalVolWeight = parseFloat(
    _resolvedPkgsForVW.map((p) => calcRowTotals(p, _initPkgMode))
      .reduce((s, r) => s + r.volWeight, 0)
      .toFixed(2)
  );
  // ─────────────────────────────────────────────────────────────────────────

  // ── Perishables temperature range/bounds resolution ────────────────────────
  // Some saved queries only stored the range code (e.g. "2-8"), not the
  // explicit numeric upperBound/lowerBound — derive them from
  // TEMPERATURE_RANGES so the pharma payload gets real numbers instead of
  // nulls for those records.
  const _resolvedTemperatureRange =
    newsearchformbackdata?.searchquery?.temperatureRange ||
    editdata?.searchquery?.temperatureRange ||
    editdata?.temperatureRange ||
    "";
  const _resolvedTemperatureRangeBounds = TEMPERATURE_RANGES.find(
    (r) => r.value === _resolvedTemperatureRange
  );
  const _resolvedUpperTempBound =
    newsearchformbackdata?.searchquery?.uppertempbound ||
    editdata?.searchquery?.uppertempbound ||
    editdata?.upperBound ||
    _resolvedTemperatureRangeBounds?.upper ||
    "";
  const _resolvedLowerTempBound =
    newsearchformbackdata?.searchquery?.lowertempbound ||
    editdata?.searchquery?.lowertempbound ||
    editdata?.lowerBound ||
    _resolvedTemperatureRangeBounds?.lower ||
    "";
  // ─────────────────────────────────────────────────────────────────────────

  // ─── Initial values ───────────────────────────────────────────────────────
  const initialValues = {
    cargoType: searchBy || "Air Cargo",

    originAirport: {
      originAirport: newsearchformbackdata?.searchquery?.originAirport.originAirport
        ? newsearchformbackdata?.searchquery?.originAirport.originAirport
        : editdata?.searchquery?.originPort?.originAirport
          ? editdata?.searchquery?.originPort?.originAirport
          : state?.originPort?.originAirport
            ? state?.originPort?.originAirport
            : "",
      originLocation: newsearchformbackdata?.searchquery?.originAirport.originLocation
        ? newsearchformbackdata?.searchquery?.originAirport.originLocation
        : editdata?.searchquery?.originPort?.originLocation
          ? editdata?.searchquery?.originPort?.originLocation
          : state?.originPort?.originLocation
            ? state?.originPort?.originLocation
            : "",
      originPickup: newsearchformbackdata?.searchquery?.originAirport.originPickup
        ? true
        : false ||
          editdata?.searchquery?.originPort?.originPickup
          ? editdata?.searchquery?.originPort?.originPickup
          : false ||
            state?.originPort?.originPickup
            ? state?.originPort?.originPickup
            : false,
      originLocationMarker: newsearchformbackdata?.searchquery?.originAirport
        .originLocationMarker
        ? newsearchformbackdata?.searchquery?.originAirport.originLocationMarker
        : editdata?.searchquery?.originPort?.originLocationMarker
          ? editdata?.searchquery?.originPort?.originLocationMarker
          : state?.originPort?.originLocationMarker
            ? state?.originPort?.originLocationMarker
            : {},
      originPortMarker: newsearchformbackdata?.searchquery?.originAirport.originPortMarker
        ? newsearchformbackdata?.searchquery?.originAirport.originPortMarker
        : editdata?.searchquery?.originPort?.originPortMarker
          ? editdata?.searchquery?.originPort?.originPortMarker
          : state?.originPort?.originPortMarker
            ? state?.originPort?.originPortMarker
            : {},
      originLocationCountry: newsearchformbackdata?.searchquery?.originAirport
        .originLocationCountry
        ? newsearchformbackdata?.searchquery?.originAirport.originLocationCountry
        : editdata?.searchquery?.originPort?.originLocationCountry
          ? editdata?.searchquery?.originPort?.originLocationCountry
          : state?.originPort?.originLocationCountry
            ? state?.originPort?.originLocationCountry
            : "",
      origincityName: newsearchformbackdata?.searchquery?.originAirport?.origincityName
        ? newsearchformbackdata?.searchquery?.originAirport?.origincityName
        : editdata?.searchquery?.originPort?.origincityName
          ? editdata?.searchquery?.originPort?.origincityName
          : state?.originPort?.origincityName
            ? state?.originPort?.origincityName
            : "",
      origincountryName: newsearchformbackdata?.searchquery?.originAirport?.origincountryName
        ? newsearchformbackdata?.searchquery?.originAirport?.origincountryName
        : editdata?.searchquery?.originPort?.origincountryName
          ? editdata?.searchquery?.originPort?.origincountryName
          : state?.originPort?.origincountryName
            ? state?.originPort?.origincountryName
            : "",
      originLogoUrl: newsearchformbackdata?.searchquery?.originAirport?.originLogoUrl
        ? newsearchformbackdata?.searchquery?.originAirport?.originLogoUrl
        : editdata?.searchquery?.originPort?.originLogoUrl
          ? editdata?.searchquery?.originPort?.originLogoUrl
          : state?.originPort?.originLogoUrl
            ? state?.originPort?.originLogoUrl
            : "",
    },

    cass: newsearchformbackdata?.searchquery?.cass
      ? newsearchformbackdata?.searchquery?.cass
      : editdata?.searchquery?.iataCass
        ? editdata?.searchquery?.iataCass
        : state?.cass
          ? state?.cass
          : "",

    destinationAirport: {
      destinationAirport: newsearchformbackdata?.searchquery?.destinationAirport
        .destinationAirport
        ? newsearchformbackdata?.searchquery?.destinationAirport.destinationAirport
        : editdata?.searchquery?.destinationPort?.destinationAirport
          ? editdata?.searchquery?.destinationPort?.destinationAirport
          : state?.destinationPort?.destinationAirport
            ? state?.destinationPort?.destinationAirport
            : "",
      destinationLocation: newsearchformbackdata?.searchquery?.destinationAirport
        .destinationLocation
        ? newsearchformbackdata?.searchquery?.destinationAirport.destinationLocation
        : editdata?.searchquery?.destinationPort?.destinationLocation
          ? editdata?.searchquery?.destinationPort?.destinationLocation
          : state?.destinationPort?.destinationLocation
            ? state?.destinationPort?.destinationLocation
            : "",
      destinationPickup: newsearchformbackdata?.searchquery?.destinationAirport
        .destinationPickup
        ? true
        : false ||
          editdata?.searchquery?.destinationPort?.destinationPickup
          ? editdata?.searchquery?.destinationPort?.destinationPickup
          : false ||
            state?.destinationPort?.destinationPickup
            ? state?.destinationPort?.destinationPickup
            : false,
      destinationLocationMarker: newsearchformbackdata?.searchquery?.destinationAirport
        .destinationLocationMarker
        ? newsearchformbackdata?.searchquery?.destinationAirport.destinationLocationMarker
        : editdata?.searchquery?.destinationPort?.destinationLocationMarker
          ? editdata?.searchquery?.destinationPort?.destinationLocationMarker
          : state?.destinationPort?.destinationLocationMarker
            ? state?.destinationPort?.destinationLocationMarker
            : {},
      destinationPortMarker: newsearchformbackdata?.searchquery?.destinationAirport
        .destinationPortMarker
        ? newsearchformbackdata?.searchquery?.destinationAirport.destinationPortMarker
        : editdata?.searchquery?.destinationPort?.destinationPortMarker
          ? editdata?.searchquery?.destinationPort?.destinationPortMarker
          : state?.destinationPort?.destinationPortMarker
            ? state?.destinationPort?.destinationPortMarker
            : {},
      destinationLocationCountry: newsearchformbackdata?.searchquery?.destinationAirport
        .destinationLocationCountry
        ? newsearchformbackdata?.searchquery?.destinationAirport.destinationLocationCountry
        : editdata?.searchquery?.destinationPort?.destinationLocationCountry
          ? editdata?.searchquery?.destinationPort?.destinationLocationCountry
          : state?.destinationPort?.destinationLocationCountry
            ? state?.destinationPort?.destinationLocationCountry
            : "",
      destinationcityName: newsearchformbackdata?.searchquery?.destinationAirport
        .destinationcityName
        ? newsearchformbackdata?.searchquery?.destinationAirport.destinationcityName
        : editdata?.searchquery?.destinationPort?.destinationcityName
          ? editdata?.searchquery?.destinationPort?.destinationcityName
          : state?.destinationPort?.destinationcityName
            ? state?.destinationPort?.destinationcityName
            : "",
      destinationcountryName: newsearchformbackdata?.searchquery?.destinationAirport
        .destinationcountryName
        ? newsearchformbackdata?.searchquery?.destinationAirport.destinationcountryName
        : editdata?.searchquery?.destinationPort?.destinationcountryName
          ? editdata?.searchquery?.destinationPort?.destinationcountryName
          : state?.destinationPort?.destinationcountryName
            ? state?.destinationPort?.destinationcountryName
            : "",
      destinationLogoUrl: newsearchformbackdata?.searchquery?.destinationAirport?.destinationLogoUrl
        ? newsearchformbackdata?.searchquery?.destinationAirport?.destinationLogoUrl
        : editdata?.searchquery?.destinationPort?.destinationLogoUrl
          ? editdata?.searchquery?.destinationPort?.destinationLogoUrl
          : state?.destinationPort?.destinationLogoUrl
            ? state?.destinationPort?.destinationLogoUrl
            : "",
    },

    goodsDetails: {
      // `searchFormData` is the KAM/admin inline "Edit Query" path (Queries.jsx):
      // the query is handed in via the prop with NO `?queryId=` URL param, so
      // `editdata` never populates and `newsearchformbackdata` is cleared on
      // mount — without this branch the date picker opened blank. Same gap, and
      // same fix, as `commodityValue` below.
      cargoreadydate: newsearchformbackdata?.searchquery?.goodsDetails?.cargoreadydate
        ? newsearchformbackdata?.searchquery?.goodsDetails?.cargoreadydate
        : editdata?.searchquery?.goodsDetails?.cargoreadydate
          ? editdata?.searchquery?.goodsDetails?.cargoreadydate
          : searchFormData?.searchquery?.goodsDetails?.cargoreadydate
            ? searchFormData?.searchquery?.goodsDetails?.cargoreadydate
            : state?.goodsDetails?.cargoreadydate
              ? state?.goodsDetails?.cargoreadydate
              : "",
    },

    // Resolve the required commodity value from EVERY edit/back source so
    // `enableReinitialize` prefills it — the dropdown displays via
    // `selectedcomondity`, but this Formik field is what the Yup schema
    // validates. Previously the `searchFormData` path and `state.commodityId`
    // (state carries `commodityId`, not `commodityValue`) were missing, so
    // editing a query back into the form left this empty and tripped the
    // "Commodity is required" error even though a commodity was shown.
    commodityValue:
      newsearchformbackdata?.commodityValue ||
      newsearchformbackdata?.commodityId ||
      editdata?.searchquery?.goodsDetails?.commodityId ||
      searchFormData?.searchquery?.goodsDetails?.commodityId ||
      state?.goodsDetails?.commodityValue ||
      state?.goodsDetails?.commodityId ||
      "",

    commodityId:
      newsearchformbackdata?.commodityId ||
      (editdata?.searchquery?.goodsDetails?.commodityId
        ? productDetails?.commodityId
        : state?.goodsDetails?.commodityId
          ? state?.goodsDetails?.commodityId
          : 115),

    commodity_description:
      newsearchformbackdata?.commodity_description ||
      (editdata?.searchquery?.goodsDetails?.commodityId
        ? productDetails?.natureOfGoods
        : state?.goodsDetails?.commodity_description
          ? state?.goodsDetails?.commodity_description
          : ""),

    commodityGroup:
      newsearchformbackdata?.commodityGroup ||
      editdata?.searchquery?.goodsDetails?.commodityGroup ||
      (editdata?.searchquery?.goodsDetails?.commodityId
        ? productDetails?.commodityGroup
        : state?.goodsDetails?.commodityGroup) ||
      "General Cargo",

    commodityCode:
      newsearchformbackdata?.commodityCode ||
      editdata?.searchquery?.goodsDetails?.commodityCode ||
      (editdata?.searchquery?.goodsDetails?.commodityId
        ? productDetails?.hscode
        : state?.goodsDetails?.commodityCode) ||
      "",

    temperatureRange: _resolvedTemperatureRange,
    uppertempbound: _resolvedUpperTempBound,
    lowertempbound: _resolvedLowerTempBound,

    packagingDetails: {
      packagingDetailsBy: newsearchformbackdata?.searchquery?.packagingDetails
        .packagingDetailsBy
        ? newsearchformbackdata?.searchquery?.packagingDetails.packagingDetailsBy
        : editdata?.searchquery?.packagingDetails?.packagingDetailsBy
          ? editdata?.searchquery?.packagingDetails?.packagingDetailsBy
          : "Packaging Type",
      shipmentTotal: {
        totalunits: newsearchformbackdata?.searchquery?.packagingDetails?.shipmentTotal
          ?.totalunits
          ? newsearchformbackdata?.searchquery?.packagingDetails.shipmentTotal.totalunits
          : editdata?.searchquery?.packagingDetails?.shipmentTotal?.totalunits
            ? editdata?.searchquery?.packagingDetails?.shipmentTotal?.totalunits
            : state?.packagingDetails?.shipmentTotal?.totalunits
              ? state?.packagingDetails?.shipmentTotal?.totalunits
              : "",
        totalvolume: newsearchformbackdata?.searchquery?.packagingDetails?.shipmentTotal
          ?.totalvolume
          ? newsearchformbackdata?.searchquery?.packagingDetails.shipmentTotal.totalvolume
          : editdata?.searchquery?.packagingDetails?.shipmentTotal?.totalvolume
            ? editdata?.searchquery?.packagingDetails?.shipmentTotal?.totalvolume
            : state?.packagingDetails?.shipmentTotal?.totalvolume
              ? state?.packagingDetails?.shipmentTotal?.totalvolume
              : "",
        unit: newsearchformbackdata?.searchquery?.packagingDetails?.shipmentTotal?.unit
          ? newsearchformbackdata?.searchquery?.packagingDetails.shipmentTotal.unit
          : editdata?.searchquery?.packagingDetails?.shipmentTotal?.unit
            ? editdata?.searchquery?.packagingDetails?.shipmentTotal?.unit
            : state?.packagingDetails?.shipmentTotal?.unit
              ? state?.packagingDetails?.shipmentTotal?.unit
              // ── IN mode: derive initial unit from packages so a re-init never ──
              // resets to "CBM" when packages are already set to inch mode.
              : PackagesInit[0]?.unit === "IN" ? "CFT" : "CBM",
        totalweight: newsearchformbackdata?.searchquery?.packagingDetails?.shipmentTotal
          ?.totalweight
          ? newsearchformbackdata?.searchquery?.packagingDetails.shipmentTotal.totalweight
          : editdata?.searchquery?.packagingDetails?.shipmentTotal?.totalweight
            ? editdata?.searchquery?.packagingDetails?.shipmentTotal?.totalweight
            : state?.packagingDetails?.shipmentTotal?.totalweight
              ? state?.packagingDetails?.shipmentTotal?.totalweight
              : "",
        weightunit: newsearchformbackdata?.searchquery?.packagingDetails?.shipmentTotal
          ?.weightunit
          ? newsearchformbackdata?.searchquery?.packagingDetails.shipmentTotal.weightunit
          : editdata?.searchquery?.packagingDetails?.shipmentTotal?.weightunit
            ? editdata?.searchquery?.packagingDetails?.shipmentTotal?.weightunit
            : state?.packagingDetails?.shipmentTotal?.weightunit
              ? state?.packagingDetails?.shipmentTotal?.weightunit
              : "KG",
        totalpackagetype: newsearchformbackdata?.searchquery?.packagingDetails?.shipmentTotal
          ?.totalpackagetype
          ? newsearchformbackdata?.searchquery?.packagingDetails.shipmentTotal.totalpackagetype
          : editdata?.searchquery?.packagingDetails?.shipmentTotal?.totalpackagetype
            ? editdata?.searchquery?.packagingDetails?.shipmentTotal?.totalpackagetype
            : state?.packagingDetails?.shipmentTotal?.totalpackagetype
              ? state?.packagingDetails?.shipmentTotal?.totalpackagetype
              : "Box",
        handling: newsearchformbackdata?.searchquery?.packagingDetails?.shipmentTotal
          ?.handling
          ? newsearchformbackdata?.searchquery?.packagingDetails.shipmentTotal.handling
          : editdata?.searchquery?.packagingDetails?.shipmentTotal?.handling
            ? editdata?.searchquery?.packagingDetails?.shipmentTotal?.handling
            : state?.packagingDetails?.shipmentTotal?.handling
              ? state?.packagingDetails?.shipmentTotal?.handling
              : "None",
        packinglistfile: editdata?.searchquery?.packagingDetails?.shipmentTotal
          ?.packinglistfile
          ? editdata?.searchquery?.packagingDetails?.shipmentTotal?.packinglistfile
          : state?.packagingDetails?.shipmentTotal?.packinglistfile
            ? state?.packagingDetails?.shipmentTotal?.packinglistfile
            : "",
        weightless: newsearchformbackdata?.searchquery?.packagingDetails?.shipmentTotal
          ?.weightless
          ? newsearchformbackdata?.searchquery?.packagingDetails.shipmentTotal.weightless
          : editdata?.searchquery?.packagingDetails?.shipmentTotal?.weightless
            ? editdata?.searchquery?.packagingDetails?.shipmentTotal?.weightless
            : state?.packagingDetails?.shipmentTotal?.weightless
              ? state?.packagingDetails?.shipmentTotal?.weightless
              : "",
        dimensionless: newsearchformbackdata?.searchquery?.packagingDetails?.shipmentTotal
          ?.dimensionless
          ? newsearchformbackdata?.searchquery?.packagingDetails.shipmentTotal.dimensionless
          : editdata?.searchquery?.packagingDetails?.shipmentTotal?.dimensionless
            ? editdata?.searchquery?.packagingDetails?.shipmentTotal?.dimensionless
            : state?.packagingDetails?.shipmentTotal?.dimensionless
              ? state?.packagingDetails?.shipmentTotal?.dimensionless
              : "",
        // ── Exact vol weight from package dims — used by result card & booking ──
        // Always derived from PackagesInit so re-inits stay consistent.
        totalvolweight: initTotalVolWeight,
        // ────────────────────────────────────────────────────────────────────────
      },
      packagingType: {
        packages: editdata?.searchquery?.packagingDetails?.packagingType?.packages?.length > 0
          ? processPackages(editdata.searchquery.packagingDetails.packagingType.packages)
          : newsearchformbackdata?.searchquery?.packagingDetails?.packagingType?.packages?.length > 0
            ? processPackages(newsearchformbackdata.searchquery.packagingDetails.packagingType.packages)
            : PackagesInit,
        totalunits: newsearchformbackdata?.searchquery?.packagingDetails?.packagingType
          ?.totalunits
          ? newsearchformbackdata?.searchquery?.packagingDetails.packagingType.totalunits
          : editdata?.searchquery?.packagingDetails?.packagingType?.totalunits
            ? editdata?.searchquery?.packagingDetails?.packagingType?.totalunits
            : state?.packagingDetails?.packagingType?.totalunits
              ? state?.packagingDetails?.packagingType?.totalunits
              : "",
        totalvolume: newsearchformbackdata?.searchquery?.packagingDetails?.packagingType
          ?.totalvolume
          ? newsearchformbackdata?.searchquery?.packagingDetails.packagingType.totalvolume
          : editdata?.searchquery?.packagingDetails?.packagingType?.totalvolume
            ? editdata?.searchquery?.packagingDetails?.packagingType?.totalvolume
            : state?.packagingDetails?.packagingType?.totalvolume
              ? state?.packagingDetails?.packagingType?.totalvolume
              : "",
        totalweight: newsearchformbackdata?.searchquery?.packagingDetails?.packagingType
          ?.totalweight
          ? newsearchformbackdata?.searchquery?.packagingDetails.packagingType.totalweight
          : editdata?.searchquery?.packagingDetails?.packagingType?.totalweight
            ? editdata?.searchquery?.packagingDetails?.packagingType?.totalweight
            : state?.packagingDetails?.packagingType?.totalweight
              ? state?.packagingDetails?.packagingType?.totalweight
              : "",
        weightMode: newsearchformbackdata?.searchquery?.packagingDetails?.packagingType
          ?.weightMode
          ? newsearchformbackdata?.searchquery?.packagingDetails.packagingType.weightMode
          : editdata?.searchquery?.packagingDetails?.packagingType?.weightMode
            ? editdata?.searchquery?.packagingDetails?.packagingType?.weightMode
            : state?.packagingDetails?.packagingType?.weightMode
              ? state?.packagingDetails?.packagingType?.weightMode
              : 1,
      },
    },

    // As with `goodsDetails.cargoreadydate` above, the `searchFormData` branch
    // covers the KAM/admin inline "Edit Query" path, where it is the only
    // populated source. `optionalVas` additionally had no `state` fallback.
    paymentTerms: {
      paymentType: newsearchformbackdata?.searchquery?.paymentTerms.paymentType
        ? newsearchformbackdata?.searchquery?.paymentTerms.paymentType
        : editdata?.searchquery?.paymentTerms?.paymentType
          ? editdata?.searchquery?.paymentTerms?.paymentType
          : searchFormData?.searchquery?.paymentTerms?.paymentType
            ? searchFormData?.searchquery?.paymentTerms?.paymentType
            : state?.paymentTerms?.paymentType
              ? state?.paymentTerms?.paymentType
              : "",
      incoTerms: newsearchformbackdata?.searchquery?.paymentTerms.incoTerms
        ? newsearchformbackdata?.searchquery?.paymentTerms.incoTerms
        : editdata?.searchquery?.paymentTerms?.incoTerms
          ? editdata?.searchquery?.paymentTerms?.incoTerms
          : searchFormData?.searchquery?.paymentTerms?.incoTerms
            ? searchFormData?.searchquery?.paymentTerms?.incoTerms
            : state?.paymentTerms?.incoTerms
              ? state?.paymentTerms?.incoTerms
              : "",
      optionalVas: newsearchformbackdata?.searchquery?.paymentTerms?.optionalVas
        ? newsearchformbackdata?.searchquery?.paymentTerms?.optionalVas
        : editdata?.searchquery?.paymentTerms?.optionalVas
          ? editdata?.searchquery?.paymentTerms?.optionalVas
          : searchFormData?.searchquery?.paymentTerms?.optionalVas
            ? searchFormData?.searchquery?.paymentTerms?.optionalVas
            : state?.paymentTerms?.optionalVas
              ? state?.paymentTerms?.optionalVas
              : [],
    },

    preferred_airline: (() => {
      // queryId (edit) flow: there is no stored preferred_airline list, so derive
      // the selection from the saved flight itineraries' carrier names, matched
      // against the loaded airline list by carr_name.
      console.log(editbookeditem,"hi4474848")
      if (queryId) {
        const itineraries = editbookeditem?.bookeditem?.flightItineraries || [];
        const ids = itineraries
          .map((it) => it?.carr_name || it?.carrierName)
          .filter(Boolean)
          .map(
            (name) =>
              airlineOptions.find(
                (a) =>
                  String(a.carr_name).toLowerCase() ===
                  String(name).toLowerCase()
              )?.value
          )
          .filter((v) => v != null);

        // Stored-rate bookings: the booked carrier may not exist in the
        // allowed-airline list, so nothing matches above. Fall back to
        // selecting it by carrier name — the tag's name + logo are then
        // resolved from the itinerary itself via airlineSelectOptions.
        if (editbookeditem?.bookeditem?.ratesFrom === "storedrate") {
          const storedIds = itineraries
            .filter((it) => {
              const name = it?.carrierName || it?.carr_name;
              if (!name) return false;
              return !airlineOptions.some(
                (a) =>
                  String(a.carr_name).toLowerCase() ===
                  String(name).toLowerCase()
              );
            })
            .map((it) => `stored:${it?.carrierName || it?.carr_name}`);
          return [...new Set([...ids, ...storedIds])];
        }
        return [...new Set(ids)];
      }
      const sources = [
        newsearchformbackdata?.searchquery?.preferred_airline,
        editdata?.searchquery?.preferred_airline,
        state?.preferred_airline,
      ];
      const raw = sources.find((s) => Array.isArray(s) && s.length > 0) || [];
      // Coerce to numbers so values match the numeric option ids (item.id),
      // otherwise round-tripped string ids ("215") won't resolve to a name/logo.
      return Array.isArray(raw)
        ? raw.map((v) => Number(v)).filter((v) => !Number.isNaN(v))
        : [];
    })(),
  };

  // ─── Validation schema ────────────────────────────────────────────────────
  const validationSchema = Yup.object().shape({
    originAirport: Yup.object().shape({
      originAirport: Yup.string().required("Origin Airport is required"),
    }),
    destinationAirport: Yup.object().shape({
      destinationAirport: Yup.string().required("Destination Airport is required"),
    }),
    goodsDetails: Yup.object().shape({
      cargoreadydate: Yup.string().required("Departure date is required"),
    }),
    commodityValue: Yup.mixed().test(
      "commodity-required",
      "Commodity is required",
      (value) => value !== undefined && value !== null && value !== ""
    ),
    temperatureRange: Yup.string().when("commodityGroup", {
      is: "Perishables",
      then: (schema) =>
        schema.required("Temperature range is required for Perishables"),
      otherwise: (schema) => schema.notRequired(),
    }),
    packagingDetails: Yup.object().shape({
      packagingDetailsBy: Yup.string().required("Packaging Details is required"),
      packagingType: Yup.object()
        .shape({
          totalunits: Yup.number(),
          totalweight: Yup.number(),
          totalvolume: Yup.number(),
          packages: Yup.array().of(
            Yup.object().shape({
              units: Yup.number()
                .typeError("Units are required")
                .required("Units are required")
                .min(1, "Must be ≥ 1"),
              length: Yup.number()
                .typeError("Length is required")
                .required("Length is required")
                .min(0.01, "Must be > 0"),
              width: Yup.number()
                .typeError("Width is required")
                .required("Width is required")
                .min(0.01, "Must be > 0"),
              height: Yup.number()
                .typeError("Height is required")
                .required("Height is required")
                .min(0.01, "Must be > 0"),
              weight: Yup.number()
                .typeError("Weight per Unit is required")
                .required("Weight per Unit is required")
                .min(0.01, "Must be > 0"),
              unit: Yup.string()
                .required("Dimension unit required")
                .oneOf(["CM", "IN"], "Dimension unit required"),
              weightunit: Yup.string()
                .required("Weight unit required")
                .oneOf(["KG/UNIT", "KG"], "Weight unit required"),
            })
          ),
        })
        .test(
          "check-packaging",
          "Total units, weight, and volume must be greater than 0",
          function (value) {
            const { totalunits, totalweight, totalvolume } = value || {};
            if (
              !totalunits ||
              totalunits <= 0 ||
              !totalweight ||
              totalweight <= 0 ||
              !totalvolume ||
              totalvolume <= 0
            ) {
              return this.createError({
                path: "packagingDetails.packagingType.totalunits",
                message: "Total units, weight, and volume must be greater than 0",
              });
            }
            return true;
          }
        ),
    }),
    paymentTerms: Yup.object().shape({
      paymentType: Yup.string().required("Payment Type is required"),
      incoTerms: Yup.string().required("Payment Terms is required"),
    }),
  });

  // ─── Preferred-airline display options ─────────────────────────────────────
  // For stored-rate edit bookings, append display-only entries (name + logo)
  // for carriers that are not present in the allowed-airline list, sourced
  // directly from the saved flight itineraries. This lets the selected tag and
  // dropdown render the booked carrier even though it has no allowed-list id.
  const storedRateAirlineOptions =
    queryId && editbookeditem?.bookeditem?.ratesFrom === "storedrate"
      ? (editbookeditem?.bookeditem?.flightItineraries || []).reduce(
          (acc, it) => {
            const name = it?.carrierName || it?.carr_name;
            if (!name) return acc;
            const inAllowed = airlineOptions.some(
              (a) =>
                String(a.carr_name).toLowerCase() ===
                String(name).toLowerCase()
            );
            const key = `stored:${name}`;
            if (inAllowed || acc.some((o) => o.value === key)) return acc;
            acc.push({
              value: key,
              label: name,
              carr_name: name,
              logoLocation: it?.carrierLogo,
            });
            return acc;
          },
          []
        )
      : [];
  const airlineSelectOptions = [
    ...airlineOptions,
    ...storedRateAirlineOptions,
  ].sort((a, b) =>
    String(a?.label ?? a?.carr_name ?? "").localeCompare(
      String(b?.label ?? b?.carr_name ?? ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  async function findflights(values) {
    // Unverified customers past their free searches stay on this page with
    // the verify-email modal instead of going to the results page.
    if (await isQueryLimitReached(userDetails)) {
      setShowVerifyModal(true);
      return;
    }
    values.searchquery = {
      ...values.searchquery,
      originAirport: {
        ...values.searchquery?.originAirport,
        originVehicleType:
          selectedVehicle?.originVehicle === ""
            ? originvehicle[0]?._id
            : selectedVehicle?.originVehicle,
        pickupToOriginAirportDistance: originDistance
          ? Number.parseFloat(originDistance)
          : 0,
      },
      destinationAirport: {
        ...values.searchquery?.destinationAirport,
        destionationVehicleType:
          selectedVehicle?.destinationVehicle === ""
            ? destinationvehicle[0]?._id
            : selectedVehicle?.destinationVehicle,
        pickupToDestionationAirportDistance: destinationDistance
          ? Number.parseFloat(destinationDistance)
          : 0,
      },
    };
    setReceivingResponse({});
    dispatch(updateStoredSearch({ nodeData: values }));
    navigate("/search-rates/search-rates-result");
  }

  const executeSearch = (values, setprops) => {
    console.log(editdata?.searchquery?.queryQuotingStatus==="Manual","37638738")
    dispatch(clearAwbNumberStore());

    // Sync pickup states into values
    values.originAirport.originPickup = originPickup;
    values.originAirport.originLocation = originLocation;
    values.originAirport.originLocationMarker = originLocationMarker;
    values.originAirport.originLocationCountry = originLocationCountry;
    values.destinationAirport.destinationPickup = destPickup;
    values.destinationAirport.destinationLocation = destinationLocation;
    values.destinationAirport.destinationLocationMarker = destinationLocationMarker;
    values.destinationAirport.destinationLocationCountry = destinationLocationCountry;

    // Stored totals must match the package rows actually being searched.
    syncTotalsFromPackages(values);

    if (type === "kam_admin" || type === "kam_admin_edit") {
      const payloadParams = {
        searchquery: {
          cargoType: values.cargoType,
          ...values,
        },
        productDetails,
        commodityId: commodityid,
        id_qr_productcode: commodityid,
        commodity_description: productDetails?.natureOfGoods,
        commodityGroup: productDetails?.commodityGroup || values.commodityGroup,
        commodityCode: productDetails?.hscode || values.commodityCode || "",
        temperatureRange: temperaturecontrol?.tempraturerange,
        uppertempbound: temperaturecontrol?.upperbound,
        lowertempbound: temperaturecontrol?.lowerbound,
        specialHandlingCodes: productDetails?.specialHandlingCodes,
        requestType: "1",
        apiCall: "WEB",
      };
      // Re-quote THIS query instead of spawning a new one. The results page
      // builds the rate-search request from `rqueryid` (redux) / `searchquery._id`
      // — NOT the top-level `payloadParams.queryId` — so the id must also be
      // published to redux via updatequeryid AND stamped inside `searchquery`,
      // otherwise the Queries edit flow searches with no queryId.
      if (searchFormData?.searchquery?._id) {
        payloadParams.queryId = searchFormData.searchquery._id;
        payloadParams.searchquery._id = searchFormData.searchquery._id;
        dispatch(updatequeryid(searchFormData.searchquery._id));
      }
      findflights(payloadParams);
      return;
    }

    const payloadParams = {
      searchquery: {
        cargoType: values.cargoType,
        ...values,
      },
      productDetails: productDetails,
      commodityId: commodityid,
      id_qr_productcode: commodityid,
      commodity_description: productDetails?.natureOfGoods,
      commodityGroup: productDetails?.commodityGroup || values.commodityGroup,
      commodityCode: productDetails?.hscode || values.commodityCode || "",
      temperatureRange: temperaturecontrol?.tempraturerange,
      uppertempbound: temperaturecontrol?.upperbound,
      lowertempbound: temperaturecontrol?.lowerbound,
      specialHandlingCodes: productDetails?.specialHandlingCodes,
      requestType:editdata?.searchquery?.queryQuotingStatus==="Manual"?"1":
        editQueryData != null ? "1" : editdata?.requestType ? "2" : "1",
      apiCall: "WEB",
    };
    if (editdata?.searchquery?.requestType&&editdata?.searchquery?.queryQuotingStatus!=="Manual") {
      payloadParams.requestType = "2";
    }
    // Queries-page "Edit" (URL has pagetype=QUERIES) must always re-quote as a
    // fresh search — send requestType "1", never "2", regardless of the
    // editdata-derived value above.
    if (isQueriesEdit) {
      payloadParams.requestType = "1";
    }
    if (editdata?.searchquery?._id) {
      payloadParams.queryId = editdata?.searchquery?._id;
      dispatch(updatequeryid(editdata?.searchquery?._id));
    }
    if (editdata?.bookeditem?.airwaybillNumber) {
      payloadParams.airWaybillNumber = editdata?.bookeditem?.airwaybillNumber;
    }
    if (searchFormData?.queryId) {
      payloadParams.queryId = searchFormData?.queryId;
    }
    if (searchFormData?.bookingId) {
      payloadParams.bookingId = searchFormData?.bookingId;
    }
    if (queryId) {
      payloadParams.bookingId = editbookeditem?.bookeditem?.bookingId;
      payloadParams.queryId = queryId;
    }
    if (editbookeditem) {
      payloadParams.bookingType = editbookeditem?.bookingType
        ? editbookeditem?.bookingType
        : editbookeditem?.search_query_data?.airlinepayloadData?.bookingType;
      payloadParams.airWaybillNumber = editbookeditem?.bookeditem?.airWaybillNumber;
      payloadParams.convId = editbookeditem?.bookeditem?.productOffers?.convId;
      payloadParams.queryId = editbookeditem?.queryId;
      payloadParams.flightitinaries = editbookeditem?.bookeditem?.flightItineraries;
      payloadParams.booking_status = editbookeditem?.booking_status;
      payloadParams.ratesFrom = editbookeditem?.bookeditem?.ratesFrom ? editbookeditem?.bookeditem?.ratesFrom : editbookeditem?.bookeditem?.flightItineraries[0]?.rates_from;
      dispatch(setAwbNumberStore(editbookeditem?.bookeditem?.airWaybillNumber));
    }
    console.log(editbookeditem?.bookeditem?.flightItineraries[0]?.rates_from, "ratesFrommmmmmmmmmmmm")
    if (
      values?.destinationAirport?.destinationPickup === true &&
      values?.originAirport?.originPickup === false &&
      values?.packagingDetails?.packagingType?.totalunits !== "" &&
      values?.packagingDetails?.packagingType?.totalvolume !== "" &&
      values?.packagingDetails?.packagingType?.totalweight !== ""
    ) {
      setIsOpen(true);
      setPopup("destination");
      setDestination(values.destinationAirport.destinationLocation);
      setSearchQuery(payloadParams);
      const destinationPayload = {
        origin: values?.destinationAirport?.destinationPortMarker || { lat: 0, lng: 0 },
        destination:
          values?.destinationAirport?.destinationLocationMarker || { lat: 0, lng: 0 },
      };
      dispatch(caldistance(destinationPayload))
        .then((res) => {
          if (!res?.payload?.success || !res?.payload?.data) {
            setIsOpen(false);
            toast.error(res?.payload?.message || "Unable to calculate the distance from required pickup or destination");
            return;
          }
          setDestinationDistance(res?.payload?.data?.distance);
          const payloadvehicle = {
            cbm: values.packagingDetails.shipmentTotal.totalvolume,
            weight: values.packagingDetails.shipmentTotal.totalweight,
            distance: parseFloat(res?.payload?.data?.distance),
          };
          dispatch(vehicletype(payloadvehicle))
            .then((res) => {
              setDestinationVehicle(res?.payload?.data);
              if (
                values.originAirport.originPickup === true &&
                values.destinationAirport.destinationPickup === true
              ) {
                setSelectedVehicle({
                  destinationVehicle: res?.payload?.data?.[0]?._id,
                  originVehicle: res?.payload?.data?.[0]?._id,
                });
              } else if (values.originAirport.originPickup === true) {
                setSelectedVehicle({
                  destinationVehicle: "",
                  originVehicle: res?.payload?.data?.[0]?._id,
                });
              } else if (values.originAirport.destinationPickup === true) {
                setSelectedVehicle({
                  destinationVehicle: res?.payload?.data?.[0]?._id,
                  originVehicle: "",
                });
              }
            })
            .catch(() => { });
        })
        .catch(() => { });
    } else if (
      values.originAirport.originPickup === true &&
      values.destinationAirport.destinationPickup === false &&
      values?.packagingDetails?.packagingType?.totalunits !== "" &&
      values?.packagingDetails?.packagingType?.totalvolume !== "" &&
      values?.packagingDetails?.packagingType?.totalweight !== ""
    ) {
      setIsOpen(true);
      setPopup("origin");
      setOrigin(values.originAirport.originLocation);
      setSearchQuery(payloadParams);
      const originPayload = {
        origin: values?.originAirport?.originLocationMarker || { lat: 0, lng: 0 },
        destination: values?.originAirport?.originPortMarker || { lat: 0, lng: 0 },
      };
      dispatch(caldistance(originPayload))
        .then((res) => {
          if (!res?.payload?.success || !res?.payload?.data) {
            setIsOpen(false);
            toast.error(res?.payload?.message || "Unable to calculate the distance from required pickup or destination");
            return;
          }
          setOriginDistance(res?.payload?.data?.distance);
          const payloadvehicle = {
            cbm: values.packagingDetails.shipmentTotal.totalvolume,
            weight: values.packagingDetails.shipmentTotal.totalweight,
            distance: parseFloat(res?.payload?.data?.distance),
          };
          dispatch(vehicletype(payloadvehicle))
            .then((res) => {
              setOriginVehicle(res?.payload?.data);
              if (
                values.originAirport.originPickup === true &&
                values.destinationAirport.destinationPickup === true
              ) {
                setSelectedVehicle({
                  destinationVehicle: res?.payload?.data?.[0]?._id,
                  originVehicle: res?.payload?.data?.[0]?._id,
                });
              } else if (values.originAirport.originPickup === true) {
                setSelectedVehicle({
                  destinationVehicle: "",
                  originVehicle: res?.payload?.data?.[0]?._id,
                });
              } else if (values.originAirport.destinationPickup === true) {
                setSelectedVehicle({
                  destinationVehicle: res?.payload?.data?.[0]?._id,
                  originVehicle: "",
                });
              }
            })
            .catch(() => { });
        })
        .catch(() => { });
    } else if (
      values.originAirport.originPickup === true &&
      values.destinationAirport.destinationPickup === true &&
      values?.packagingDetails?.packagingType?.totalunits !== "" &&
      values?.packagingDetails?.packagingType?.totalvolume !== "" &&
      values?.packagingDetails?.packagingType?.totalweight !== ""
    ) {
      setIsOpen(true);
      setPopup("both");
      setOrigin(values.originAirport.originLocation);
      setDestination(values.destinationAirport.destinationLocation);
      setSearchQuery(payloadParams);
      const originPayload = {
        origin: values?.originAirport?.originLocationMarker || { lat: 0, lng: 0 },
        destination: values?.originAirport?.originPortMarker || { lat: 0, lng: 0 },
      };
      const destinationPayload = {
        origin: values?.destinationAirport?.destinationPortMarker || { lat: 0, lng: 0 },
        destination:
          values?.destinationAirport?.destinationLocationMarker || { lat: 0, lng: 0 },
      };
      dispatch(caldistance(originPayload))
        .then((res) => {
          if (!res?.payload?.success || !res?.payload?.data) {
            setIsOpen(false);
            toast.error(res?.payload?.message || "Unable to calculate the distance from required pickup or destination");
            return;
          }
          setOriginDistance(res?.payload?.data?.distance);
          const payloadvehicle = {
            cbm: values.packagingDetails.shipmentTotal.totalvolume,
            weight: values.packagingDetails.shipmentTotal.totalweight,
            distance: parseFloat(res?.payload?.data?.distance),
          };
          dispatch(vehicletype(payloadvehicle))
            .then((res) => {
              setOriginVehicle(res?.payload?.data);
              if (
                values.originAirport.originPickup === true &&
                values.destinationAirport.destinationPickup === true
              ) {
                setSelectedVehicle({
                  destinationVehicle: res?.payload?.data?.[0]?._id,
                  originVehicle: res?.payload?.data?.[0]?._id,
                });
              } else if (values.originAirport.originPickup === true) {
                setSelectedVehicle({
                  destinationVehicle: "",
                  originVehicle: res?.payload?.data?.[0]?._id,
                });
              } else if (values.originAirport.destinationPickup === true) {
                setSelectedVehicle({
                  destinationVehicle: res?.payload?.data?.[0]?._id,
                  originVehicle: "",
                });
              }
            })
            .catch(() => { });
        })
        .catch(() => { });
      dispatch(caldistance(destinationPayload))
        .then((res) => {
          if (!res?.payload?.success || !res?.payload?.data) {
            setIsOpen(false);
            toast.error(res?.payload?.message || "Unable to calculate the distance from required pickup or destination");
            return;
          }
          setDestinationDistance(res?.payload?.data?.distance);
          const payloadvehicle2 = {
            cbm: values.packagingDetails.shipmentTotal.totalvolume,
            weight: values.packagingDetails.shipmentTotal.totalweight,
            distance: parseFloat(res?.payload?.data?.distance),
          };
          dispatch(vehicletype(payloadvehicle2))
            .then((res) => {
              setDestinationVehicle(res?.payload?.data);
              if (
                values.originAirport.originPickup === true &&
                values.destinationAirport.destinationPickup === true
              ) {
                setSelectedVehicle({
                  destinationVehicle: res?.payload?.data?.[0]?._id,
                  originVehicle: res?.payload?.data?.[0]?._id,
                });
              } else if (values.originAirport.originPickup === true) {
                setSelectedVehicle({
                  destinationVehicle: "",
                  originVehicle: res?.payload?.data?.[0]?._id,
                });
              } else if (values.originAirport.destinationPickup === true) {
                setSelectedVehicle({
                  destinationVehicle: res?.payload?.data?.[0]?._id,
                  originVehicle: "",
                });
              }
            })
            .catch(() => { });
        })
        .catch(() => { });
    } else {
      findflights(payloadParams);
    }
  };

  const onSubmit = (values, setprops) => {
    executeSearch(values, setprops);
  };

  useEffect(() => {
    if (allAirportsState?.allairports?.length > 0) {
      let optionData = [];
      allAirportsState?.allairports.map((item) => {
        return optionData.push({
          value: `${item.cityName} - (${item.code})`,
          label: `${item.cityName} - (${item.code})`,
          origincityName: item?.cityName,
          origincountryName: item?.countryName,
          country_logo_url: item?.country_logo_url,
          lat: item?.lat,
          lon: item?.lon,
        });
      });
      setAirports(optionData);
    } else {
      dispatch(getallairports());
    }
  }, [dispatch, allAirportsState]);

  useEffect(() => {
    if (Array.isArray(allAirportsState?.originairports) && allAirportsState.originairports.length > 0) {
      const optionData = allAirportsState.originairports.map((item) => ({
        value: `${item.cityName} - (${item.code})`,
        label: `${item.cityName} - (${item.code})`,
        origincityName: item?.cityName,
        origincountryName: item?.countryName,
        country_logo_url: item?.country_logo_url,
        lat: item?.lat,
        lon: item?.lon,
        iataCass: item?.cass ?? "",
      }));
      setOriginAirports(optionData);
    } else if (!Array.isArray(allAirportsState?.originairports) && !hasFetchedOriginAirports.current) {
      hasFetchedOriginAirports.current = true;
      dispatch(getOriginAirports());
    }
  }, [dispatch, allAirportsState?.originairports]);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 1024) {
        setWebOpen(false);
      } else {
        setWebOpen(true);
      }
    }
    window.addEventListener("resize", handleResize);
    window.addEventListener("load", handleResize);
    handleResize();
  }, []);

  useEffect(() => {
    if (hasnodata?.statusCode === 500) {
      setError(hasnodata);
    }
  }, [hasnodata]);

  const handleproceed = () => {
    findflights(searchquery);
    setIsOpen(false);
  };

  // ─── Airport options for destination (filter same country as origin) ──────

  return error === "error" ? (
    <FailureSearchRate />
  ) : (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(formik) => {
          formikRef.current = formik;
          if (!selectedcomondity && formik?.values?.commodityValue) {
            setSelectedComondity(formik?.values?.commodityValue);
          }

          const packages =
            formik.values.packagingDetails?.packagingType?.packages || [];

          // Derived totals from all package rows
          const pkgWeightMode = packages[0]?.weightunit === "KG" ? 2 : 1;
          const allTotals = packages.map((p) => calcRowTotals(p, pkgWeightMode));
          const totalUnits = allTotals.reduce((s, r) => s + r.totalsubunits, 0);
          const totalWeight = allTotals.reduce((s, r) => s + r.totalsubweight, 0);
          const totalVolume = allTotals.reduce((s, r) => s + r.totalsubvolume, 0);
          const totalVolWeight = allTotals.reduce((s, r) => s + r.volWeight, 0);
          const chargeableWeight = Math.max(totalWeight, totalVolWeight);

          // ── IN mode: display totals ────────────────────────────────────────
          // isInchMode drives all IN-specific UI; totalCFT is used in footer/row summary.
          // These are view-only — no existing calculation is touched.
          const isInchMode = packages[0]?.unit === "IN";
          const totalCFT = isInchMode
            ? allTotals.reduce((s, r) => s + (r.cft || 0), 0)
            : null;
          // ─────────────────────────────────────────────────────────────────────

          const allTurnable = packages.length > 0 &&
            packages.every(
              (p) =>
                p.handling?.includes("Tiltable") &&
                !p.handling?.includes("Non-Tiltable")
            );
          const allStackable = packages.length > 0 &&
            packages.every(
              (p) =>
                p.handling?.includes("Stackable") &&
                !p.handling?.includes("Non-stackable")
            );

          const recalcAllTotals = (pkgs, form) => {
            const mode = pkgs[0]?.weightunit === "KG" ? 2 : 1;
            const totals = pkgs.map((p) => calcRowTotals(p, mode));
            const tUnits = totals.reduce((s, r) => s + r.totalsubunits, 0);
            const tWeight = totals.reduce((s, r) => s + r.totalsubweight, 0);
            const tVolume = totals.reduce((s, r) => s + r.totalsubvolume, 0);

            // ── IN mode: API payload totalvolume = CFT (L×B×H×units/1728) ──────
            // When all rows are in inches, send CFT as the totalvolume instead of CBM.
            // CM behaviour is unchanged — tVolume (CBM) is used as before.
            const isInchMode = pkgs[0]?.unit === "IN";
            const tVolumeFinal = isInchMode
              ? totals.reduce((s, r) => s + (r.cft || 0), 0)
              : tVolume;

            // Store the exact vol weight (sum of per-row volWeight which uses
            // integer-rounded cm dims for IN mode). Downstream display components
            // read this directly instead of re-deriving from CFT, which would give
            // a slightly different result due to rounding differences.
            const tVolWeight = parseFloat(
              totals.reduce((s, r) => s + r.volWeight, 0).toFixed(2)
            );
            // ─────────────────────────────────────────────────────────────────────

            form.setFieldValue("packagingDetails.packagingType.totalunits", tUnits);
            form.setFieldValue("packagingDetails.packagingType.totalweight", tWeight);
            form.setFieldValue("packagingDetails.packagingType.totalvolume", tVolumeFinal);
            form.setFieldValue("packagingDetails.shipmentTotal.totalunits", tUnits);
            form.setFieldValue("packagingDetails.shipmentTotal.totalweight", tWeight);
            form.setFieldValue("packagingDetails.shipmentTotal.totalvolume", tVolumeFinal);
            form.setFieldValue("packagingDetails.shipmentTotal.totalvolweight", tVolWeight);
            // ── IN mode: set volume unit in payload to "CFT"; CM stays "CBM" ───
            form.setFieldValue("packagingDetails.shipmentTotal.unit", isInchMode ? "CFT" : "CBM");
            // ─────────────────────────────────────────────────────────────────────
            form.setFieldValue(
              "packagingDetails.packagingDetailsBy",
              "Packaging Type"
            );
          };

          const toggleRowHandling = (idx, type, pkgs, form) => {
            const pkg = pkgs[idx];
            const handling = pkg.handling || "Tiltable and Stackable";
            let isTiltable = handling.includes("Tiltable") && !handling.includes("Non-Tiltable");
            let isStackable =
              handling.includes("Stackable") && !handling.includes("Non-stackable");

            if (type === "turnable") {
              isTiltable = !isTiltable;
            } else {
              isStackable = !isStackable;
            }

            const newHandling =
              (isTiltable ? "Tiltable" : "Non-Tiltable") +
              " and " +
              (isStackable ? "Stackable" : "Non-stackable");

            const base = `packagingDetails.packagingType.packages[${idx}]`;
            form.setFieldValue(`${base}.handling`, newHandling);
            form.setFieldValue(`${base}.turnable`, isTiltable);
            form.setFieldValue(`${base}.stackable`, isStackable);
          };

          const inputStyle = {
            height: 34,
            boxSizing: "border-box",
            border: "1px solid #e7e0d6",
            borderRadius: 5,
            textAlign: "center",
            fontSize: 13,
            width: "100%",
            outline: "none",
            padding: "0 4px",
          };


          return (
            <Form autoComplete="off">
              <div className="searchRatesForm" style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>

                {/* ── Row 1: Itinerary + Goods Type side by side ───────────────── */}
                <div style={{ display: "flex", gap: 10, alignItems: "stretch"}}>

                  {/* Itinerary Card */}
                  <div style={{ ...cardStyle, flex: "0 0 58%" }}>
                    <SectionHeader title="Itinerary" icon={MdFlightTakeoff} />
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>

                      {/* Column 1: Origin Airport */}
                      {/* flex "1 1 0" so origin & destination share equal width and stay aligned */}
                      <div style={{ flex: "1 1 0", minWidth: 0 }}>
                        <label style={fieldLabelStyle}>
                          ORIGIN AIRPORT <span style={{ color: "#e11d48" }}>*</span>
                        </label>
                        <ReactSelect
                          styles={reactSelectStyles}
                          options={originAirports}
                          placeholder="Search origin..."
                          // Disable origin selection when opened from a queryId URL
                          isDisabled={queryIdLock || fieldsDisabled}
                          value={
                            originAirports.find(
                              (a) => a.value === formik.values.originAirport.originAirport
                            ) ||
                            (formik.values.originAirport.originAirport
                              ? {
                                value: formik.values.originAirport.originAirport,
                                label: formik.values.originAirport.originAirport,
                              }
                              : null)
                          }
                          onChange={(option) => handleOriginChange(option, formik)}
                        />
                        <ErrorMessage
                          name="originAirport.originAirport"
                          component={TextError}
                        />
                        <div style={{ marginTop: 5 }}>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: 12,
                              color: "#57534e",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={originPickup}
                              // Disable pickup checkbox when opened from a queryId URL
                              disabled={queryIdLock || fieldsDisabled}
                              onChange={(e) => setOriginPickup(e.target.checked)}
                              style={{ accentColor: "#ffaf00" }}
                            />
                            Require pickup from origin location
                          </label>
                        </div>
                        {originPickup && (
                          <div style={{ marginTop: 8 }}>
                            <label style={fieldLabelStyle}>ORIGIN LOCATION</label>
                            <Autocomplete
                              apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
                              defaultValue={originLocation}
                              disabled={fieldsDisabled}
                              onPlaceSelected={(place) => {
                                const addr = place?.formatted_address || "";
                                const lat =
                                  place?.geometry?.location?.lat() || 0;
                                const lng =
                                  place?.geometry?.location?.lng() || 0;
                                const country =
                                  place?.address_components?.find((c) =>
                                    c.types.includes("country")
                                  )?.long_name || "";
                                setOriginLocation(addr);
                                setOriginLocationMarker({ lat, lng });
                                setOriginLocationCountry(country);
                              }}
                              style={{
                                width: "100%",
                                height: 40,
                                border: "1px solid #e7e0d6",
                                borderRadius: 7,
                                padding: "0 10px",
                                fontSize: 13,
                                background: "#fafaf9",
                                outline: "none",
                              }}
                              options={{ types: ["geocode"] }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Column 2: Destination Airport */}
                      {/* flex "1 1 0" so origin & destination share equal width and stay aligned */}
                      <div style={{ flex: "1 1 0", minWidth: 0 }}>
                        <label style={fieldLabelStyle}>
                          DESTINATION AIRPORT <span style={{ color: "#e11d48" }}>*</span>
                        </label>
                        <ReactSelect
                          styles={reactSelectStyles}
                          options={airports.filter(
                            (a) =>
                              a.origincountryName !==
                              formik.values.originAirport.origincountryName
                          )}
                          placeholder="Search destination..."
                          // Disable destination selection when opened from a queryId URL
                          isDisabled={queryIdLock || fieldsDisabled}
                          value={
                            airports.find(
                              (a) =>
                                a.value ===
                                formik.values.destinationAirport.destinationAirport
                            ) || null
                          }
                          onChange={(option) => handleDestinationChange(option, formik)}
                        />
                        <ErrorMessage
                          name="destinationAirport.destinationAirport"
                          component={TextError}
                        />
                        <div style={{ marginTop: 5 }}>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: 12,
                              color: "#57534e",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={destPickup}
                              // Disable road transportation checkbox when opened from a queryId URL
                              disabled={queryIdLock || fieldsDisabled}
                              onChange={(e) => setDestPickup(e.target.checked)}
                              style={{ accentColor: "#ffaf00" }}
                            />
                            Require road transportation at destination
                          </label>
                        </div>
                        {destPickup && (
                          <div style={{ marginTop: 8 }}>
                            <label style={fieldLabelStyle}>DESTINATION LOCATION</label>
                            <Autocomplete
                              apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
                              defaultValue={destinationLocation}
                              disabled={fieldsDisabled}
                              onPlaceSelected={(place) => {
                                const addr = place?.formatted_address || "";
                                const lat =
                                  place?.geometry?.location?.lat() || 0;
                                const lng =
                                  place?.geometry?.location?.lng() || 0;
                                const country =
                                  place?.address_components?.find((c) =>
                                    c.types.includes("country")
                                  )?.long_name || "";
                                setDestinationLocation(addr);
                                setDestinationLocationMarker({ lat, lng });
                                setDestinationLocationCountry(country);
                              }}
                              style={{
                                width: "100%",
                                height: 40,
                                border: "1px solid #e7e0d6",
                                borderRadius: 7,
                                padding: "0 10px",
                                fontSize: 13,
                                background: "#fafaf9",
                                outline: "none",
                              }}
                              options={{ types: ["geocode"] }}
                            />
                          </div>
                        )}
                      </div>

                    </div>

                    {/* Departure Date — below Origin & Destination */}
                    <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <label style={fieldLabelStyle}>
                          DEPARTURE DATE <span style={{ color: "#e11d48" }}>*</span>
                        </label>
                        <DatePicker
                          format="DD/MM/YYYY"
                          disabled={fieldsDisabled}
                          allowClear
                          placeholder="DD/MM/YYYY"
                          style={{
                            width: "100%",
                            height: 34,
                            fontSize: 13.5,
                            background: "#fafaf9",
                            borderColor: "#e7e0d6",
                            borderRadius: "7px",
                          }}
                          // Allow up to 3 months, but not beyond the current year.
                          disabledDate={(current) => {
                            const today = dayjs();
                            const maxDate = dayjs.min(
                              today.add(3, "month"),
                              today.endOf("year")
                            );
                            return (
                              current &&
                              (current < today.startOf("day") ||
                                current > maxDate.endOf("day"))
                            );
                          }}
                          value={
                            formik.values.goodsDetails.cargoreadydate
                              ? dayjs(
                                formik.values.goodsDetails.cargoreadydate,
                                "DD/MM/YYYY"
                              )
                              : null
                          }
                          onChange={(date) => {
                            const formatted =
                              date && dayjs(date).isValid()
                                ? dayjs(date).format("DD/MM/YYYY")
                                : "";
                            formik.setFieldValue(
                              "goodsDetails.cargoreadydate",
                              formatted
                            );
                            setSelectedDate(formatted);
                          }}
                        />
                        <ErrorMessage
                          name="goodsDetails.cargoreadydate"
                          component={TextError}
                        />
                      </div>
                      <div style={{ flex: 1 }} />
                    </div>
                  </div>

                  {/* Right column: Goods Type + Preferred Airline stacked */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>

                  {/* Goods Type Card */}
                  <div style={{ ...cardStyle }}>
                    <SectionHeader title="Goods Type" icon={MdCategory} />

                    <label style={fieldLabelStyle}>
                      COMMODITY <span style={{ color: "#e11d48" }}>*</span>
                    </label>
                    <ReactSelect
                      styles={reactSelectStyles}
                      options={comodityMaster}
                      formatOptionLabel={formatCommodityOptionLabel}
                      placeholder="Select commodity..."
                      value={
                        comodityMaster.find(
                          (o) =>
                            o.value ===
                            (selectedcomondity || formik.values.commodityValue)
                        ) || null
                      }
                     
                      isDisabled={fieldsDisabled}
                      onChange={(option) => {
                        comoditydatabasedonqr_code(option);
                        formik.setFieldValue("commodityId", option.commodityId);
                        formik.setFieldValue("commodityValue", option.value);
                        formik.setFieldValue(
                          "commodity_description",
                          option.commodity_description
                        );
                        formik.setFieldValue("commodityGroup", option.commodityGroup);
                        formik.setFieldValue("commodityCode", option.hscode || "");
                        if (option?.commodityGroup !== "Perishables") {
                          formik.setFieldValue("temperatureRange", "");
                          formik.setFieldValue("lowertempbound", "");
                          formik.setFieldValue("uppertempbound", "");
                          setTemperatureControl({
                            tempraturerange: "",
                            lowerbound: "",
                            upperbound: "",
                          });
                        }
                        setSelectedComondity(option.value);

                      }}
                    />
                    <ErrorMessage name="commodityValue" component={TextError} />

                    {/* Temperature range toggle buttons — "Perishables" only.
                        "Perishables No Temp" is temperature-less by definition. */}
                    {productDetails?.commodityGroup === "Perishables" && (
                        <div
                          style={{
                            background: "#fffbf0",
                            border: "1px solid #f0e8d5",
                            borderRadius: 10,
                            padding: "12px 14px",
                            marginTop: 14,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: "#a8a29e",
                              letterSpacing: ".06em",
                              textTransform: "uppercase",
                              marginBottom: 10,
                            }}
                          >
                            Temperature Range
                            <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {TEMPERATURE_RANGES.map((range) => {
                              const isSelected =
                                temperaturecontrol.tempraturerange === range.value;
                              return (
                                <button
                                  key={range.value}
                                  type="button"
                                  disabled={fieldsDisabled}
                                  onClick={() => {
                                    if (isSelected) {
                                      setTemperatureControl({
                                        tempraturerange: "",
                                        lowerbound: "",
                                        upperbound: "",
                                      });
                                      formik.setFieldValue("temperatureRange", "");
                                      formik.setFieldValue("lowertempbound", "");
                                      formik.setFieldValue("uppertempbound", "");
                                    } else {
                                      setTemperatureControl({
                                        tempraturerange: range.value,
                                        lowerbound: range.lower,
                                        upperbound: range.upper,
                                      });
                                      formik.setFieldValue("temperatureRange", range.value);
                                      formik.setFieldValue("lowertempbound", range.lower);
                                      formik.setFieldValue("uppertempbound", range.upper);
                                    }
                                  }}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 7,
                                    height: 36,
                                    padding: "0 14px",
                                    borderRadius: 8,
                                    border: isSelected
                                      ? "1.5px solid #ffaf00"
                                      : "1.5px solid #e7e0d6",
                                    background: isSelected ? "#ffaf00" : "#fff",
                                    color: isSelected ? "#fff" : "#292524",
                                    fontSize: 13,
                                    fontWeight: isSelected ? 600 : 500,
                                    cursor: "pointer",
                                    boxShadow: isSelected
                                      ? "0 2px 8px rgba(255,175,0,.3)"
                                      : "none",
                                  }}
                                >
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
                                  </svg>
                                  {range.label}
                                </button>
                              );
                            })}
                          </div>
                          {!temperaturecontrol?.tempraturerange && (
                              <p
                                style={{
                                  color: "#ef4444",
                                  fontSize: 11,
                                  marginTop: 6,
                                }}
                              >
                                Temperature range is required for Perishables
                              </p>
                            )}
                        </div>
                      )}
                

                  {/* Preferred Airline Card — moved below Goods Type */}
              
                    <div 
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: 10,
                      }}
                    >
                      <SectionHeader title="Preferred Airline" icon={MdFlightTakeoff} />
                      {Array.isArray(formik.values.preferred_airline) &&
                        formik.values.preferred_airline.length > 0 && (
                          <span
                            style={{
                              background: "#ffaf00",
                              color: "#fff",
                              fontSize: 12,
                              fontWeight: 700,
                              padding: "5px 14px",
                              borderRadius: 20,
                              boxShadow: "0 2px 8px rgba(255,175,0,0.30)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formik.values.preferred_airline.length} selected
                          </span>
                        )}
                    </div>
                    <label style={fieldLabelStyle}>FILTER BY CARRIER</label>
                    <ReactSelect
                      isMulti
                      styles={airlineSelectStyles}
                      options={airlineSelectOptions}
                      formatOptionLabel={formatAirlineOptionLabel}
                      placeholder="All carriers..."
                      // Deliberately NOT gated by `fieldsDisabled`: on the
                      // Queries "Query Detail" screen every other field is
                      // read-only, but KAM/admin need to narrow carriers before
                      // hitting "Search Rate". The selection rides into the
                      // search via `searchquery: { ...values }` in executeSearch.
                      // `queryIdLock` still applies — a deep-linked booking edit
                      // (?queryId=) must keep its carriers frozen.
                      isDisabled={queryIdLock}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      value={airlineSelectOptions.filter((o) =>
                        (Array.isArray(formik.values.preferred_airline)
                          ? formik.values.preferred_airline
                          : []
                        ).some((v) => String(v) === String(o.value))
                      )}
                      onChange={(selected) =>
                        formik.setFieldValue(
                          "preferred_airline",
                          (selected || []).map((o) => o.value)
                        )
                      }
                    />
                  </div>

                  </div> {/* end right column */}
                </div> {/* end Itinerary + Goods Type row */}

                {/* ── Row 2: Shipment Details ───────────────────────────────────── */}
                <div style={cardStyle}>
                  <FieldArray name="packagingDetails.packagingType.packages">
                    {({ push, remove, form }) => {
                      const pkgs =
                        form.values.packagingDetails?.packagingType?.packages || [];

                      /**
                       * Applies autofill rows from the drawer to the Formik packages array and
                       * immediately recomputes all shipment totals + syncs Redux weightMode so
                       * nothing is left stale after the autofill.
                       *
                       * Mirrors the manual weight-unit change handler (around the dispatch call
                       * below the table) which does:
                       *   1. recalcAllTotals          — aggregate totals
                       *   2. setFieldValue weightMode — Formik packaging fields
                       *   3. dispatch updateWeightMode — Redux global
                       *
                       * @param {Array<object>} rows - Validated rows from AutofillUnitsDrawer.
                       */
                      const handleAutofillApply = (rows) => {
                        if (!rows.length) return;

                        // All rows share the same weight type (validated by the drawer).
                        // "KG" = total shipment weight (mode 2); "KG/UNIT" = per-unit (mode 1).
                        const newWeightMode = rows[0].weightunit === "KG" ? 2 : 1;

                        const mapped = rows.map((r) => {
                          const tiltPart = r.turnable !== false ? "Tiltable" : "Non-Tiltable";
                          const stackPart = r.stackable !== false ? "Stackable" : "Non-stackable";
                          const pkg = {
                            units: String(r.units),
                            length: String(r.length),
                            width: String(r.width),
                            height: String(r.height),
                            unit: r.unit || "CM",
                            weight: String(r.weight),
                            weightunit: r.weightunit || "KG/UNIT",
                            packagetype: "Box",
                            handling: `${tiltPart} and ${stackPart}`,
                            temp: "",
                          };
                          // Pre-compute per-row sub-totals so the table cells are correct
                          // immediately after apply, matching what handleFieldChange does for
                          // manual edits. Without this, cells show "" until the user edits a field.
                          const rowTotals = calcRowTotals(pkg, newWeightMode);
                          return {
                            ...pkg,
                            totalsubunits: String(rowTotals.totalsubunits),
                            totalsubweight: rowTotals.totalsubweight.toFixed(2),
                            totalsubvolume: String(rowTotals.totalsubvolume),
                          };
                        });

                        form.setFieldValue(
                          "packagingDetails.packagingType.packages",
                          mapped
                        );

                        // Recompute all aggregate totals (totalunits, totalweight, totalvolume,
                        // totalvolweight, shipmentTotal.*) so the summary row is in sync.
                        recalcAllTotals(mapped, form);

                        // Sync weightMode to Formik + Redux so subsequent calculations and API
                        // submission use the correct per-unit vs. total-weight interpretation.
                        form.setFieldValue("packagingDetails.packagingType.weightMode", newWeightMode);
                        form.setFieldValue("packagingDetails.shipmentTotal.type", newWeightMode);
                        dispatch(updateWeightMode(newWeightMode));
                      };

                      return (
                        <>
                          <AutofillUnitsDrawer
                            open={autofillDrawerOpen}
                            onClose={() => setAutofillDrawerOpen(false)}
                            onApplyRows={handleAutofillApply}
                          />

                          {/* Header row: title + All Turnable + All Stackable + Autofill Units, all on one line */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 10,
                              flexWrap: "wrap",
                              gap: 8,
                            }}
                          >
                            <SectionHeader title="Shipment Details" icon={MdInventory2} style={{ marginBottom: 0 }} />

                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              {/* All Turnable — preserves each row's stackable state */}
                              <button
                                type="button"
                                disabled={fieldsDisabled}
                                onClick={() => {
                                  const nextTurnable = !allTurnable;
                                  const newPkgs = pkgs.map((p) => {
                                    const rowStack = p.handling?.includes("Stackable") && !p.handling?.includes("Non-stackable");
                                    return {
                                      ...p,
                                      turnable: nextTurnable,
                                      handling: (nextTurnable ? "Tiltable" : "Non-Tiltable") + " and " + (rowStack ? "Stackable" : "Non-stackable"),
                                    };
                                  });
                                  form.setFieldValue("packagingDetails.packagingType.packages", newPkgs);
                                }}
                                style={{
                                  padding: "6px 14px",
                                  borderRadius: 20,
                                  border: allTurnable ? "none" : "1.5px solid #e7e0d6",
                                  background: allTurnable ? "#ffaf00" : "#fff",
                                  color: allTurnable ? "#fff" : "#57534e",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 7,
                                }}
                              >
                                {allTurnable ? (
                                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                                    <path d="M2 7l4 4 6-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                ) : (
                                  <span style={{
                                    width: 14, height: 14,
                                    borderRadius: "50%",
                                    border: "1.5px solid #c4b8a8",
                                    display: "inline-block",
                                    flexShrink: 0,
                                  }} />
                                )}
                                All Turnable
                              </button>

                              {/* All Stackable — preserves each row's turnable state */}
                              <button
                                type="button"
                                                                                    disabled={fieldsDisabled}

                                onClick={() => {
                                  const nextStackable = !allStackable;
                                  const newPkgs = pkgs.map((p) => {
                                    const rowTurn = p.handling?.includes("Tiltable") && !p.handling?.includes("Non-Tiltable");
                                    return {
                                      ...p,
                                      stackable: nextStackable,
                                      handling: (rowTurn ? "Tiltable" : "Non-Tiltable") + " and " + (nextStackable ? "Stackable" : "Non-stackable"),
                                    };
                                  });
                                  form.setFieldValue("packagingDetails.packagingType.packages", newPkgs);
                                }}
                                style={{
                                  padding: "6px 14px",
                                  borderRadius: 20,
                                  border: allStackable ? "none" : "1.5px solid #e7e0d6",
                                  background: allStackable ? "#ffaf00" : "#fff",
                                  color: allStackable ? "#fff" : "#57534e",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 7,
                                }}
                              >
                                {allStackable ? (
                                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                                    <path d="M2 7l4 4 6-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                ) : (
                                  <span style={{
                                    width: 14, height: 14,
                                    borderRadius: "50%",
                                    border: "1.5px solid #c4b8a8",
                                    display: "inline-block",
                                    flexShrink: 0,
                                  }} />
                                )}
                                All Stackable
                              </button>

                              {/* Autofill Units */}
                              <button
                                type="button"
                                disabled={fieldsDisabled}
                                onClick={() => setAutofillDrawerOpen(true)}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "7px 16px",
                                  borderRadius: 20,
                                  border: "none",
                                  background: "#ffaf00",
                                  color: "#fff",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  letterSpacing: ".02em",
                                  boxShadow: "0 2px 8px rgba(255,175,0,0.30)",
                                  flexShrink: 0,
                                }}
                              >
                                ✨ Autofill Units
                              </button>
                            </div>
                          </div>

                          {/* Table */}
                          <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid #f0e8d5", overflow: "hidden" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                              <thead>
                                <tr
                                  style={{
                                    background: "#ffaf00",
                                    color: "#fff",
                                    fontSize: 13,
                                    fontWeight: 700,
                                    letterSpacing: ".04em",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  <th style={{ padding: "10px 8px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.25)" }}>UNITS</th>
                                  <th style={{ padding: "10px 8px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.25)" }}>
                                    DIMENSIONS (L × B × H)
                                  </th>
                                  <th style={{ padding: "10px 8px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.25)" }}>WEIGHT</th>
                                  <th style={{ padding: "10px 8px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.25)" }}>TURN.</th>
                                  <th style={{ padding: "10px 8px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.25)" }}>STACK.</th>
                                  <th style={{ padding: "10px 8px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.25)" }}>
                                    ROW SUMMARY
                                  </th>
                                  <th style={{ padding: "10px 8px", textAlign: "left" }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {pkgs.map((pkg, idx) => {
                                  const rowWeightMode = pkgs[0]?.weightunit === "KG" ? 2 : 1;
                                  const rowTotals = calcRowTotals(pkg, rowWeightMode);
                                  const isTurnable =
                                    pkg.handling?.includes("Tiltable") &&
                                    !pkg.handling?.includes("Non-Tiltable");
                                  const isStackable =
                                    pkg.handling?.includes("Stackable") &&
                                    !pkg.handling?.includes("Non-stackable");

                                  const pkgErrors =
                                    form.errors?.packagingDetails?.packagingType?.packages?.[idx] || {};

                                  const handleFieldChange = (field, value) => {
                                    const updatedPkg = { ...pkg, [field]: value };
                                    form.setFieldValue(
                                      `packagingDetails.packagingType.packages[${idx}].${field}`,
                                      value
                                    );
                                    if (pkgErrors[field]) {
                                      form.setFieldError(
                                        `packagingDetails.packagingType.packages[${idx}].${field}`,
                                        undefined
                                      );
                                    }
                                    const rowTotals = calcRowTotals(updatedPkg, rowWeightMode);
                                    const base = `packagingDetails.packagingType.packages[${idx}]`;
                                    form.setFieldValue(`${base}.totalsubunits`, String(rowTotals.totalsubunits));
                                    form.setFieldValue(`${base}.totalsubweight`, rowTotals.totalsubweight.toFixed(2));
                                    form.setFieldValue(`${base}.totalsubvolume`, String(rowTotals.totalsubvolume));
                                    const updatedPkgs = pkgs.map((p, i) =>
                                      i === idx ? { ...updatedPkg, totalsubunits: String(rowTotals.totalsubunits), totalsubweight: rowTotals.totalsubweight.toFixed(2), totalsubvolume: String(rowTotals.totalsubvolume) } : p
                                    );
                                    recalcAllTotals(updatedPkgs, form);
                                  };

                                  return (
                                    <tr
                                      key={idx}
                                      style={{
                                        background: idx % 2 === 0 ? "#fff" : "#fffdf7",
                                        borderBottom: "1px solid #f0e8d5",
                                      }}
                                    >
                                      {/* Units */}
                                      <td style={{ padding: "6px 6px", textAlign: "center", verticalAlign: "middle", borderRight: "1px solid #f0e8d5" }}>
                                        <input
                                          type="number"
                                          value={pkg.units}
                                          placeholder="Qty"
                                          onChange={(e) =>
                                            handleFieldChange("units", e.target.value)
                                          }
                                            disabled={fieldsDisabled}
                                          onWheel={(e) => e.target.blur()}
                                          onKeyDown={ (e) => {
              if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
            }}

                                          style={{ ...inputStyle, width: 60, borderColor: pkgErrors.units ? "#ef4444" : "#e7e0d6" }}
                                          min="0"
                                        />
                                        {pkgErrors.units && (
                                          <div style={{ color: "#ef4444", fontSize: 10, marginTop: 2 }}>{pkgErrors.units}</div>
                                        )}
                                      </td>

                                      {/* Dimensions */}
                                      <td style={{ padding: "6px 10px", verticalAlign: "middle", borderRight: "1px solid #f0e8d5" }}>
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 18,
                                          }}
                                        >
                                          <div style={{ display: "flex", flexDirection: "column" }}>
                                            <input
                                              type="number"
                                              value={pkg.length}
                                              onChange={(e) =>
                                                handleFieldChange("length", e.target.value)
                                              }
                                                disabled={fieldsDisabled}
                                              onWheel={(e) => e.target.blur()}
                                              onKeyDown={ (e) => {
              if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
            }}

                                              style={{ ...inputStyle, width: 64, borderColor: pkgErrors.length ? "#ef4444" : "#e7e0d6" }}
                                              placeholder="L"
                                              min="0"
                                            />
                                            {pkgErrors.length && (
                                              <div style={{ color: "#ef4444", fontSize: 10, marginTop: 2 }}>{pkgErrors.length}</div>
                                            )}
                                          </div>
                                          <span style={{ color: "#a8a29e", fontSize: 11, paddingTop: 8 }}>×</span>
                                          <div style={{ display: "flex", flexDirection: "column" }}>
                                            <input
                                              type="number"
                                              value={pkg.width}
                                              onChange={(e) =>
                                                handleFieldChange("width", e.target.value)
                                              }
                                                disabled={fieldsDisabled}
                                              onWheel={(e) => e.target.blur()}
onKeyDown={ (e) => {
              if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
            }}
                                              style={{ ...inputStyle, width: 64, borderColor: pkgErrors.width ? "#ef4444" : "#e7e0d6" }}
                                              placeholder="B"
                                              min="0"
                                            />
                                            {pkgErrors.width && (
                                              <div style={{ color: "#ef4444", fontSize: 10, marginTop: 2 }}>{pkgErrors.width}</div>
                                            )}
                                          </div>
                                          <span style={{ color: "#a8a29e", fontSize: 11, paddingTop: 8 }}>×</span>
                                          <div style={{ display: "flex", flexDirection: "column" }}>
                                            <input
                                              type="number"
                                              value={pkg.height}
                                              onChange={(e) =>
                                                handleFieldChange("height", e.target.value)
                                              }
                                                disabled={fieldsDisabled}
                                              onWheel={(e) => e.target.blur()}
onKeyDown={ (e) => {
              if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
            }}
                                              style={{ ...inputStyle, width: 64, borderColor: pkgErrors.height ? "#ef4444" : "#e7e0d6" }}
                                              placeholder="H"
                                              min="0"
                                            />
                                            {pkgErrors.height && (
                                              <div style={{ color: "#ef4444", fontSize: 10, marginTop: 2 }}>{pkgErrors.height}</div>
                                            )}
                                          </div>
                                          {/* <select
                                            value={pkgs[0]?.unit || "CM"}
                                            disabled={idx !== 0||fieldsDisabled}
                                            
                                            onKeyDown={(e) => {
                                              // Restrict changing the value via keyboard; allow Tab to move focus
                                              if (e.key !== "Tab") e.preventDefault();
                                            }}
                                            onChange={(e) => {
                                              const newUnit = e.target.value;
                                              pkgs.forEach((_, i) => {
                                                form.setFieldValue(
                                                  `packagingDetails.packagingType.packages[${i}].unit`,
                                                  newUnit
                                                );
                                              });
                                              // ── Recalc payload totalvolume on CM ↔ IN switch ──────────────────
                                              // Build updated pkgs with the new unit so recalcAllTotals
                                              // uses the correct formula (CFT vs CBM) immediately.
                                              const updatedPkgs = pkgs.map((p) => ({ ...p, unit: newUnit }));
                                              recalcAllTotals(updatedPkgs, form);
                                              // ──────────────────────────────────────────────────────────────────
                                            }}
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              height: 34,
                                              padding: "0 6px",
                                              border: "1px solid #e7e0d6",
                                              borderRadius: 5,
                                              fontSize: 13,
                                              fontWeight: 600,
                                              color: "#57534e",
                                              background: idx === 0 ? "#f5f5f4" : "#ebebeb",
                                              cursor: idx === 0 ? "pointer" : "not-allowed",
                                              opacity: idx === 0 ? 1 : 0.6,
                                            }}
                                          >
                                            <option value="CM">CM</option>
                                            <option value="IN">IN</option>
                                          </select> */}

                                          <div style={{ display: "flex", flexDirection: "column" }} title="Select dimension unit">
                                          <ReactSelect
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder="Select dim. unit"
                                            value={[
                                              { value: "CM", label: "CM" },
                                              { value: "IN", label: "IN" },
                                            ].find((o) => o.value === pkgs[0]?.unit) || null}
                                            isDisabled={idx !== 0 || fieldsDisabled}
                                            isSearchable={false}
                                            onChange={(option) => {
                                              const newUnit = option.value;
                                              pkgs.forEach((_, i) => {
                                                form.setFieldValue(
                                                  `packagingDetails.packagingType.packages[${i}].unit`,
                                                  newUnit
                                                );
                                              });
                                              const updatedPkgs = pkgs.map((p) => ({ ...p, unit: newUnit }));
                                              recalcAllTotals(updatedPkgs, form);
                                            }}
                                            options={[
                                              { value: "CM", label: "CM" },
                                              { value: "IN", label: "IN" },
                                            ]}
                                            styles={{
                                              ...reactSelectStyles,
                                              container: (base) => ({
                                                ...base,
                                                width: 150,
                                                minWidth: 150,
                                                opacity: idx !== 0 ? 0.6 : 1,
                                              }),
                                              placeholder: (base) => ({
                                                ...base,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                              }),
                                              control: (base) => ({
                                                ...base,
                                                minHeight: 34,
                                                height: 34,
                                                boxSizing: "border-box",
                                                borderColor: idx === 0 && pkgErrors.unit ? "#ef4444" : "#e7e0d6",
                                                borderRadius: 5,
                                                background: idx === 0 ? "#f5f5f4" : "#ebebeb",
                                                boxShadow: "none",
                                                cursor: idx !== 0 ? "not-allowed" : "pointer",
                                                "&:hover": { borderColor: "#ffaf00" },
                                              }),
                                              valueContainer: (base) => ({
                                                ...base,
                                                padding: "0 6px",
                                                height: 34,
                                              }),
                                              singleValue: (base) => ({
                                                ...base,
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: "#57534e",
                                              }),
                                              indicatorSeparator: () => ({ display: "none" }),
                                              dropdownIndicator: (base) => ({
                                                ...base,
                                                padding: "0 4px",
                                                color: "#a8a29e",
                                              }),
                                            }}
                                          />
                                          {idx === 0 && pkgErrors.unit && (
                                            <div style={{ color: "#ef4444", fontSize: 10, marginTop: 2 }}>{pkgErrors.unit}</div>
                                          )}
                                          </div>
                                        </div>
                                        {/* ── IN mode: show cm equivalents below dimension inputs ──────────── */}
                                        {/* Purely informational — does not affect any calculation */}
                                        {pkg.unit === "IN" && (parseFloat(pkg.length) > 0 || parseFloat(pkg.width) > 0 || parseFloat(pkg.height) > 0) && (
                                          <div style={{ fontSize: 10, color: "#a8a29e", marginTop: 4, textAlign: "left", whiteSpace: "nowrap" }}>
                                            {roundDim(parseFloat(pkg.length || 0) * 2.54)} cm ×{" "}
                                            {roundDim(parseFloat(pkg.width || 0) * 2.54)} cm ×{" "}
                                            {roundDim(parseFloat(pkg.height || 0) * 2.54)} cm
                                          </div>
                                        )}
                                        {/* ─────────────────────────────────────────────────────────────────── */}
                                      </td>

                                      {/* Weight */}
                                      <td style={{ padding: "6px 10px", verticalAlign: "middle", borderRight: "1px solid #f0e8d5" }}>
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 18,
                                          }}
                                        >
                                          <div style={{ display: "flex", flexDirection: "column" }}>
                                            <input
                                              type="number"
                                              value={pkg.weight}
                                              onChange={(e) =>
                                                handleFieldChange("weight", e.target.value)
                                              }
                                                                                          disabled={fieldsDisabled}

                                              onWheel={(e) => e.target.blur()}
                                              
onKeyDown={ (e) => {
              if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
            }}
                                              style={{ ...inputStyle, width: 84, borderColor: pkgErrors.weight ? "#ef4444" : "#e7e0d6" }}
                                              placeholder="Weight"
                                              min="0"
                                              step="any"
                                            />
                                            {pkgErrors.weight && (
                                              <div style={{ color: "#ef4444", fontSize: 10, marginTop: 2 }}>{pkgErrors.weight}</div>
                                            )}
                                          </div>
                                          {/* <AntSelect
                                            value={pkgs[0]?.weightunit || "KG/UNIT"}
                                            disabled={idx !== 0||fieldsDisabled}
                                            showSearch={false}
                                            onKeyDown={(e) => {
                                              // Restrict changing the value via keyboard; allow Tab to move focus
                                              if (e.key !== "Tab") e.preventDefault();
                                            }}
                                            onChange={(val) => {
                                              if (idx !== 0) return;
                                              const modeId = val === "KG" ? 2 : 1;
                                              const updatedPkgs = pkgs.map((p) => {
                                                const rt = calcRowTotals({ ...p, weightunit: val }, modeId);
                                                return {
                                                  ...p,
                                                  weightunit: val,
                                                  totalsubunits: String(rt.totalsubunits),
                                                  totalsubweight: rt.totalsubweight.toFixed(2),
                                                  totalsubvolume: String(rt.totalsubvolume),
                                                };
                                              });
                                              form.setFieldValue(
                                                "packagingDetails.packagingType.packages",
                                                updatedPkgs
                                              );
                                              recalcAllTotals(updatedPkgs, form);
                                              console.log(modeId, "modid")
                                              form.setFieldValue("packagingDetails.packagingType.weightMode", modeId);
                                              form.setFieldValue("packagingDetails.shipmentTotal.type", modeId);
                                              dispatch(updateWeightMode(modeId));
                                            }}
                                            style={{ width: 102, height: 34, opacity: idx !== 0 ? 0.5 : 1 }}
                                            options={[
                                              { value: "KG/UNIT", label: "KG/UNIT" },
                                              { value: "KG", label: "TOTAL" },
                                            ]}
                                          /> */}

                                          <div style={{ display: "flex", flexDirection: "column" }} title="Select weight unit">
                                          <ReactSelect
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder="Select wt unit"
                                            value={[
                                              { value: "KG/UNIT", label: "KG/UNIT" },
                                              { value: "KG", label: "TOTAL" },
                                            ].find((o) => o.value === pkgs[0]?.weightunit) || null}
                                            isDisabled={idx !== 0 || fieldsDisabled}
                                            isSearchable={false}
                                            onChange={(option) => {
                                              if (idx !== 0) return;
                                              const val = option.value;
                                              const modeId = val === "KG" ? 2 : 1;
                                              const updatedPkgs = pkgs.map((p) => {
                                                const rt = calcRowTotals({ ...p, weightunit: val }, modeId);
                                                return {
                                                  ...p,
                                                  weightunit: val,
                                                  totalsubunits: String(rt.totalsubunits),
                                                  totalsubweight: rt.totalsubweight.toFixed(2),
                                                  totalsubvolume: String(rt.totalsubvolume),
                                                };
                                              });
                                              form.setFieldValue("packagingDetails.packagingType.packages", updatedPkgs);
                                              recalcAllTotals(updatedPkgs, form);
                                              form.setFieldValue("packagingDetails.packagingType.weightMode", modeId);
                                              form.setFieldValue("packagingDetails.shipmentTotal.type", modeId);
                                              dispatch(updateWeightMode(modeId));
                                            }}
                                            options={[
                                              { value: "KG/UNIT", label: "KG/UNIT" },
                                              { value: "KG", label: "TOTAL" },
                                            ]}
                                            styles={{
                                              ...reactSelectStyles,
                                              container: (base) => ({
                                                ...base,
                                                width: 150,
                                                minWidth: 150,
                                                opacity: idx !== 0 ? 0.5 : 1,
                                              }),
                                              placeholder: (base) => ({
                                                ...base,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                              }),
                                              control: (base) => ({
                                                ...base,
                                                minHeight: 34,
                                                height: 34,
                                                boxSizing: "border-box",
                                                borderColor: idx === 0 && pkgErrors.weightunit ? "#ef4444" : "#e7e0d6",
                                                borderRadius: 5,
                                                background: "#fafaf9",
                                                boxShadow: "none",
                                                cursor: idx !== 0 ? "not-allowed" : "pointer",
                                                "&:hover": { borderColor: "#ffaf00" },
                                              }),
                                              valueContainer: (base) => ({
                                                ...base,
                                                padding: "0 6px",
                                                height: 34,
                                              }),
                                              singleValue: (base) => ({
                                                ...base,
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: "#57534e",
                                              }),
                                              indicatorSeparator: () => ({ display: "none" }),
                                              dropdownIndicator: (base) => ({
                                                ...base,
                                                padding: "0 4px",
                                                color: "#a8a29e",
                                              }),
                                            }}
                                          />
                                          {idx === 0 && pkgErrors.weightunit && (
                                            <div style={{ color: "#ef4444", fontSize: 10, marginTop: 2 }}>{pkgErrors.weightunit}</div>
                                          )}
                                          </div>
                                        </div>
                                      </td>

                                      {/* Turnable */}
                                      <td style={{ padding: "6px 6px", textAlign: "center", borderRight: "1px solid #f0e8d5" }}>
                                        <button
                                          type="button"
                                          onClick={() => toggleRowHandling(idx, "turnable", pkgs, form)}
                                          title={isTurnable ? "Turnable — click to disable" : "Not turnable — click to enable"}
                                                                                            disabled={fieldsDisabled}

                                          style={{
                                            width: 34, height: 34,
                                            borderRadius: 8,
                                            border: isTurnable ? "none" : "1.5px solid #e7e0d6",
                                            background: isTurnable ? "#ffaf00" : "#fafaf9",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            margin: "0 auto",
                                          }}
                                        >
                                          {isTurnable ? (
                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                              <path d="M2 7l4 4 6-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          ) : (
                                            <span style={{
                                              width: 14, height: 14,
                                              borderRadius: "50%",
                                              border: "1.5px solid #c4b8a8",
                                              display: "inline-block",
                                            }} />
                                          )}
                                        </button>
                                      </td>

                                      {/* Stackable */}
                                      <td style={{ padding: "6px 6px", textAlign: "center", borderRight: "1px solid #f0e8d5" }}>
                                        <button
                                          type="button"
                                          onClick={() => toggleRowHandling(idx, "stackable", pkgs, form)}
                                          title={isStackable ? "Stackable — click to disable" : "Not stackable — click to enable"}
                                                                                             disabled={fieldsDisabled}

                                          style={{
                                            width: 34, height: 34,
                                            borderRadius: 8,
                                            border: isStackable ? "none" : "1.5px solid #e7e0d6",
                                            background: isStackable ? "#ffaf00" : "#fafaf9",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            margin: "0 auto",
                                          }}
                                        >
                                          {isStackable ? (
                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                              <path d="M2 7l4 4 6-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          ) : (
                                            <span style={{
                                              width: 14, height: 14,
                                              borderRadius: "50%",
                                              border: "1.5px solid #c4b8a8",
                                              display: "inline-block",
                                            }} />
                                          )}
                                        </button>
                                      </td>

                                      {/* Row Summary */}
                                      <td style={{ padding: "6px 14px", borderRight: "1px solid #f0e8d5" }}>
                                        <div style={{ display: "flex", flexWrap: "nowrap", alignItems: "center", gap: 18, minWidth: 150, whiteSpace: "nowrap" }}>
                                          {[
                                            { label: "UNITS", val: rowTotals.totalsubunits },
                                            // ── IN mode: show CFT with CBM equivalent in parens ─────────────
                                            // CM mode is unchanged (label "CBM", value totalsubvolume).
                                            pkg.unit === "IN"
                                              ? {
                                                label: "CFT",
                                                val: (rowTotals.cft || 0).toFixed(2) +
                                                  " (" + (rowTotals.cbmFromInch || 0).toFixed(2) + " CBM)",
                                              }
                                              : { label: "CBM", val: rowTotals.totalsubvolume },
                                            // ────────────────────────────────────────────────────────────────
                                            { label: "G.W", val: parseFloat(rowTotals.totalsubweight).toFixed(2) + " kg" },
                                            { label: "V.W", val: rowTotals.volWeight + " kg" },
                                          ].map((item) => (
                                            <div key={item.label}>
                                              <div style={{ fontSize: 9, fontWeight: 700, color: "#a8a29e", letterSpacing: ".06em", textTransform: "uppercase" }}>{item.label}</div>
                                              <div style={{ fontSize: 12, fontWeight: 700, color: "#292524" }}>{item.val}</div>
                                            </div>
                                          ))}
                                        </div>
                                      </td>

                                      {/* Actions */}
                                      <td style={{ padding: "6px 6px", textAlign: "center" }}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
                                          {idx === 0 && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                push({
                                                  units: "",
                                                  length: "",
                                                  width: "",
                                                  height: "",
                                                  unit: pkgs[0]?.unit || "",
                                                  weight: "",
                                                  weightunit: pkgs[0]?.weightunit || "",
                                                  packagetype: "Box",
                                                  handling: "Non-Tiltable and Stackable",
                                                  turnable: false,
                                                  stackable: true,
                                                  totalsubunits: "",
                                                  totalsubweight: "",
                                                  totalsubvolume: "",
                                                });
                                              }}
                                              style={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: 9,
                                                border: "none",
                                                background: "#292524",
                                                color: "#fff",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                                                                                  disabled={fieldsDisabled}

                                            >
                                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M8 3v10M3 8h10" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                                              </svg>
                                            </button>
                                          )}
                                          {idx > 0 && (
                                            <button
                                              type="button"
                                              disabled={fieldsDisabled}
                                              onClick={() => {
                                                remove(idx);
                                                const newPkgs = pkgs.filter((_, i) => i !== idx);
                                                recalcAllTotals(newPkgs, form);
                                              }}
                                              style={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: 9,
                                                border: "1.5px solid #fecaca",
                                                background: "#fff5f5",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                <path d="M10 11v6M14 11v6" />
                                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                              </svg>
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* Footer totals */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              gap: 0,
                              flexWrap: "wrap",
                              marginTop: 14,
                              padding: "8px 20px",
                              background: "#fafaf9",
                              borderRadius: 10,
                              border: "1px solid #f0e8d5",
                            }}
                          >
                            {[
                              { label: "TOTAL UNITS", val: String(totalUnits) },
                              // ── IN mode: footer shows CFT; CM mode unchanged (CBM) ────────────────
                              isInchMode
                                ? { label: "CFT", val: (totalCFT || 0).toFixed(2) }
                                : { label: "CBM", val: totalVolume.toFixed(2) },
                              // ─────────────────────────────────────────────────────────────────────
                              {
                                label: "GROSS WT.",
                                val: totalWeight.toFixed(2) + " kg",
                              },
                              {
                                label: "VOL. WT.",
                                val: totalVolWeight.toFixed(2) + " kg",
                              },
                              {
                                label: "CHARG. WT.",
                                val: Math.ceil(chargeableWeight) + " kg",
                              },
                            ].map((item, idx, arr) => (
                              <div
                                key={item.label}
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  flex: 1,
                                  padding: "0 16px",
                                  borderRight: idx < arr.length - 1 ? "1px solid #e7e0d6" : "none",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 11, 
                                    fontWeight: 700,
                                    color: "#a8a29e",
                                    letterSpacing: ".08em",
                                    textTransform: "uppercase",
                                    marginBottom: 0,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.label}
                                </span>
                                <span
                                  style={{
                                    fontSize: 15,
                                    lineHeight: 1,
                                    fontWeight: 700,
                                    color: "#292524",
                                    letterSpacing: "-.01em",
                                  }}
                                >
                                  {item.val}
                                </span>
                              </div>
                            ))}
                          </div>
                          {/* ── IN mode: disclaimer shown below footer totals ────────────────── */}
                          {/* Rates platform expects CM-based inputs; this note mirrors the      */}
                          {/* reference UI to inform users their inch values are noted.          */}
                          {isInchMode && (
                            <div style={{
                              marginTop: 8,
                              textAlign: "center",
                              fontSize: 11,
                              color: "#78716c",
                              fontStyle: "italic",
                            }}>
                              rates will be provided in centimeters (CM) per kilo (KG)
                            </div>
                          )}
                          {/* ─────────────────────────────────────────────────────────────────── */}
                        </>
                      );
                    }}
                  </FieldArray>

                  <ErrorMessage
                    name="packagingDetails.packagingType.totalunits"
                    component={TextError}
                  />
                </div>

                {/* ── Row 3: Payment Terms ──────────────────────────────────────── */}


                <div style={cardStyle}>
                  <SectionHeader title="Payment Terms" icon={MdPayment} />
                  <div
                    style={{
                      display: "flex",
                      gap: 16,
                      flexWrap: "wrap",
                      // Align the fields by their TOP (labels + selects) so that an
                      // inline validation error under one field only grows downward
                      // and never pushes its select box out of line with the others
                      // (flex-end did the opposite — the erroring fields rode up).
                      alignItems: "flex-start",
                    }}
                  >
                    {/* Payment Type */}
                    <div style={{ flex: "1 1 160px" }}>
                      <label style={fieldLabelStyle}>
                        PAYMENT TYPE <span style={{ color: "#e11d48" }}>*</span>
                      </label>
                      {/* <AntSelect
                        value={formik.values.paymentTerms.paymentType || undefined}
                        placeholder="Select payment type"
                        showSearch={false}
                        onKeyDown={(e) => {
                          // Restrict changing the value via keyboard; allow Tab to move focus
                          if (e.key !== "Tab") e.preventDefault();
                        }}
                        onChange={(val) =>
                          formik.setFieldValue("paymentTerms.paymentType", val)
                        }
                                disabled={fieldsDisabled}
                        style={{ width: "100%", minHeight: 42 }}
                        options={[
                          { value: "Prepaid", label: "Prepaid" },
                          { value: "Collect", label: "Collect" },
                        ]}
                      /> */}

                      <ReactSelect
                        styles={reactSelectStyles}
                        placeholder="Select payment type"
                        isSearchable={false}
                        isDisabled={fieldsDisabled}
                        value={
                          formik.values.paymentTerms.paymentType
                            ? { value: formik.values.paymentTerms.paymentType, label: formik.values.paymentTerms.paymentType }
                            : null
                        }
                        onChange={(option) =>
                          formik.setFieldValue("paymentTerms.paymentType", option?.value)
                        }
                        options={[
                          { value: "Prepaid", label: "Prepaid" },
                          { value: "Collect", label: "Collect" },
                        ]}
                      />
                      <ErrorMessage name="paymentTerms.paymentType" component={TextError} />
                    </div>

                    {/* Incoterms */}
                    <div style={{ flex: "1 1 160px" }}>
                      <label style={fieldLabelStyle}>
                        INCOTERMS <span style={{ color: "#e11d48" }}>*</span>
                      </label>
                      {/* <AntSelect
                        value={formik.values.paymentTerms.incoTerms || undefined}
                        placeholder="Select incoterms"
                        showSearch={false}
                                disabled={fieldsDisabled}
                        onKeyDown={(e) => {
                          // Restrict changing the value via keyboard; allow Tab to move focus
                          if (e.key !== "Tab") e.preventDefault();
                        }}
                        onChange={(val) => {
                          formik.setFieldValue("paymentTerms.incoTerms", val);
                          getoptionalvas({
                            ...formik.values,
                            paymentTerms: {
                              ...formik.values.paymentTerms,
                              incoTerms: val,
                            },
                          });
                        }}
                        style={{ width: "100%", minHeight: 42 }}
                        options={incoTermsOpts.flatMap((g) =>
                          g.options
                            ? g.options.map((o) => ({ value: o.value, label: o.label }))
                            : [{ value: g.value, label: g.label }]
                        )}
                      /> */}
                      <ReactSelect
                        styles={reactSelectStyles}
                        placeholder="Select incoterms"
                        isSearchable={false}
                        isDisabled={fieldsDisabled}
                        value={
                          formik.values.paymentTerms.incoTerms
                            ? { value: formik.values.paymentTerms.incoTerms, label: formik.values.paymentTerms.incoTerms }
                            : null
                        }
                        onChange={(option) => {
                          formik.setFieldValue("paymentTerms.incoTerms", option?.value);
                          getoptionalvas({
                            ...formik.values,
                            paymentTerms: {
                              ...formik.values.paymentTerms,
                              incoTerms: option?.value,
                            },
                          });
                        }}
                        options={(incoTermsOpts||[]).flatMap((g) =>
                          (g?.options||[]).map((o) => ({ value: o.value, label: o.label }))

                        )}
                      />
                      <ErrorMessage name="paymentTerms.incoTerms" component={TextError} />
                    </div>

                    {/* Optional VAS */}
                    <div style={{ flex: "2 1 220px" }}>
                      <label style={fieldLabelStyle}>OPTIONAL VAS</label>
                      {/* <AntSelect
                        mode="multiple"
                                disabled={fieldsDisabled}
                        value={
                          Array.isArray(formik.values.paymentTerms.optionalVas)
                            ? formik.values.paymentTerms.optionalVas
                            : []
                        }
                        placeholder="Select optional services"
                        onChange={(selectedValues) => {
                          formik.setFieldValue(
                            "paymentTerms.optionalVas",
                            selectedValues
                          );
                          setVasOptionData(
                            vasOptions.filter((v) =>
                              selectedValues.includes(v.vatId)
                            )
                          );
                        }}
                        style={{ width: "100%", minHeight: 42 }}
                        options={vasOptions.map((item) => ({
                          value: item.vatId,
                          label: `${item.type}-${item.chargeHeadName}`,
                        }))}
                      /> */}

                      <ReactSelect
                        styles={reactSelectStyles}
                        isMulti
                        placeholder="Select optional services"
                        isDisabled={fieldsDisabled}
                        isLoading={vasLoading}
                        // Multi-check UX: checkbox in each option, keep the menu
                        // open across selections, keep already-selected rows
                        // visible (with their check), and collapse the chips past
                        // the 2nd into a "+N" chip that lists the rest on hover.
                        components={{
                          Option: CheckboxOption,
                          MultiValue: OverflowMultiValue,
                        }}
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        // Incoterms defaults to CFR and rarely changes, so relying
                        // on its onChange alone left this list empty. Fetch fresh
                        // VAS from the current origin/destination/weight/incoterms
                        // whenever the user opens the dropdown.
                        onMenuOpen={() => getoptionalvas(formik.values)}
                        noOptionsMessage={() =>
                          vasLoading
                            ? "Loading…"
                            : !formik.values.originAirport?.originAirport ||
                              !formik.values.destinationAirport?.destinationAirport ||
                              !formik.values.paymentTerms?.incoTerms
                            ? "Select origin, destination & incoterms first"
                            : "No optional services available"
                        }
                        value={
                          Array.isArray(formik.values.paymentTerms.optionalVas)
                            ? vasOptions
                              .filter((v) => formik.values.paymentTerms.optionalVas.includes(v.vatId))
                              .map((v) => ({ value: v.vatId, label: `${v.type}-${v.chargeHeadName}` }))
                            : []
                        }
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions.map((o) => o.value);
                          formik.setFieldValue("paymentTerms.optionalVas", selectedValues);
                          setVasOptionData(vasOptions.filter((v) => selectedValues.includes(v.vatId)));
                        }}
                        options={vasOptions.map((item) => ({
                          value: item.vatId,
                          label: `${item.type}-${item.chargeHeadName}`,
                        }))}
                      />
                    </div>
                  </div>
                </div>

                {/* ── Row 4: Search Button ─────────────────────────────────────── */}
                {!fieldsDisabled && (
                  <button
                    type="submit"
                    // disabled={isBranchEmpty}
                    onClick={() => {
                      // validateOnChange/validateOnBlur are off, so an invalid
                      // required field (often the Origin/Destination Airport or
                      // Commodity fields above this button) otherwise fails the
                      // submit with zero feedback. Surface the first Yup message
                      // as a toast so the click never looks like it did nothing.
                      formik.validateForm().then((errs) => {
                        const [firstMessage] = flattenFormikErrors(errs);
                        if (firstMessage) {
                          toast.error(firstMessage);
                        }
                      });
                    }}
                    className={`w-full h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 bg-[#ffaf00] text-white hover:bg-[#e09b00]`}
                    // className={`w-full h-12 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${
                    //   isBranchEmpty
                    //     ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    //     : "bg-[#ffaf00] text-white hover:bg-[#e09b00]"
                    // }`}
                    style={{ boxShadow: "0 4px 14px rgba(255,175,0,0.35)" }}
                  >
                    <SearchOutlined />
                    Search Flights &amp; Rates
                  </button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>

      {/* ── Vehicle confirmation modals ─────────────────────────────────────── */}
      {isOpen && popup === "destination" && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-120">
            <div className="flex justify-start pt-2 pl-2 bg-gray-100 text-[#f5aa06] text-2xl rounded-md text-center items-center border-b pb-2">
              Confirm delivery and Vehicle Type
            </div>
            <p className="text-gray-500 mt-4">
              Since you have requested door pickup and delivery, please confirm
              <br />
              the vehicle type that suits your requirement.
            </p>
            <div className="space-y-4 flex flex-col justify-between items-center w-fit py-4">
              <div className="space-y-4 flex justify-between items-center py-4">
                <div className="p-2 m-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Destination Location <span className="block">(_ of Goods)</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    defaultValue={destination}
                    disabled={true}
                  />
                </div>
                <div className="p-2 m-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Distance
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    disabled={true}
                    defaultValue={destinationDistance}
                  />
                </div>
                <div className="p-2 m-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Type
                  </label>
                  {/* <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    value={selectedVehicle?.destinationVehicle}
                    onChange={(e) =>
                      setSelectedVehicle((prev) => ({
                        ...prev,
                        destinationVehicle: e.target.value,
                      }))
                    }
                  >
                    <option value="">
                      {destinationvehicle[0]?.name || "Vehicle Type"}
                    </option>
                    {destinationvehicle?.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.name}
                      </option>
                    ))}
                  </select> */}
                  <ReactSelect
                    styles={reactSelectStyles}
                    placeholder="Vehicle Type"
                    value={
                      destinationvehicle
                        .map((v) => ({ value: v._id, label: v.name }))
                        .find((o) => o.value === selectedVehicle?.destinationVehicle) || null
                    }
                    onChange={(option) =>
                      setSelectedVehicle((prev) => ({
                        ...prev,
                        destinationVehicle: option?.value,
                      }))
                    }
                    options={destinationvehicle.map((item) => ({
                      value: item._id,
                      label: item.name,
                    }))}
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pr-12">
              <button
                type="button"
                onClick={handlemodalclose}
                className="px-4 py-2 border rounded bg-black text-white hover:bg-yellow-600"
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 border rounded bg-yellow-600 text-white hover:bg-black"
                onClick={handleproceed}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {isOpen && popup === "origin" && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-120">
            <div className="flex justify-start pt-2 pl-2 bg-gray-100 text-[#f5aa06] text-2xl rounded-md text-center items-center border-b pb-2">
              Confirm delivery and Vehicle Type
            </div>
            <p className="text-gray-500 mt-4">
              Since you have requested door pickup and delivery, please confirm
              <br />
              the vehicle type that suits your requirement.
            </p>
            <div className="space-y-4 flex flex-col justify-between items-center w-fit py-4">
              <div className="space-y-4 flex justify-between items-center py-4">
                <div className="p-2 m-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Origin Location <span className="block">(_ of Goods)</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    defaultValue={origin}
                    disabled={true}
                  />
                </div>
                <div className="p-2 m-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Distance
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    defaultValue={originDistance}
                    disabled={true}
                  />
                </div>
                <div className="p-2 m-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Type
                  </label>
                  {/* <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    value={selectedVehicle?.originVehicle}
                    onChange={(e) =>
                      setSelectedVehicle((prev) => ({
                        ...prev,
                        originVehicle: e.target.value,
                      }))
                    }
                  >
                    <option value={originvehicle[0]?._id}>
                      {originvehicle[0]?.name || "Vehicle Type"}
                    </option>
                    {originvehicle?.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.name}
                      </option>
                    ))}
                  </select> */}
                  <ReactSelect
                    styles={reactSelectStyles}
                    placeholder="Vehicle Type"
                    value={
                      originvehicle
                        .map((v) => ({ value: v._id, label: v.name }))
                        .find((o) => o.value === selectedVehicle?.originVehicle) || null
                    }
                    onChange={(option) =>
                      setSelectedVehicle((prev) => ({
                        ...prev,
                        originVehicle: option?.value,
                      }))
                    }
                    options={originvehicle.map((item) => ({
                      value: item._id,
                      label: item.name,
                    }))}
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pr-12">
              <button
                type="button"
                onClick={handlemodalclose}
                className="px-4 py-2 border rounded bg-black text-white hover:bg-yellow-600"
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 border rounded bg-yellow-600 text-white hover:bg-black"
                onClick={handleproceed}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {isOpen && popup === "both" && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-120">
            <div className="flex justify-start pt-2 pl-2 bg-gray-100 text-[#f5aa06] text-2xl rounded-md text-center items-center border-b pb-2">
              Confirm delivery and Vehicle Type
            </div>
            <p className="text-gray-500 mt-4">
              Since you have requested door pickup and delivery, please confirm
              <br />
              the vehicle type that suits your requirement.
            </p>
            <div className="space-y-4 flex flex-col justify-between items-center w-fit py-4">
              <div className="space-y-4 flex justify-between items-center">
                <div className="p-2 m-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Destination Location <span className="block">(_ of Goods)</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                    defaultValue={destination}
                    disabled={true}
                  />
                </div>
                <div className="p-2 m-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Distance
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                    disabled={true}
                    defaultValue={destinationDistance}
                  />
                </div>
                <div className="p-2 m-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Type
                  </label>
                  {/* <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                    value={selectedVehicle?.destinationVehicle}
                    onChange={(e) =>
                      setSelectedVehicle((prev) => ({
                        ...prev,
                        destinationVehicle: e.target.value,
                      }))
                    }
                  >
                    <option value={destinationvehicle[0]?._id}>
                      {destinationvehicle[0]?.name || "Vehicle Type"}
                    </option>
                    {destinationvehicle?.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.name}
                      </option>
                    ))}
                  </select> */}
                  <ReactSelect
                    styles={reactSelectStyles}
                    placeholder="Vehicle Type"
                    value={
                      destinationvehicle
                        .map((v) => ({ value: v._id, label: v.name }))
                        .find((o) => o.value === selectedVehicle?.destinationVehicle) || null
                    }
                    onChange={(option) =>
                      setSelectedVehicle((prev) => ({
                        ...prev,
                        destinationVehicle: option?.value,
                      }))
                    }
                    options={destinationvehicle.map((item) => ({
                      value: item._id,
                      label: item.name,
                    }))}
                  />
                </div>
              </div>
              <div className="space-y-4 flex justify-between items-center">
                <div className="p-2 m-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Origin Location <span className="block">(_ of Goods)</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                    defaultValue={origin}
                    disabled={true}
                  />
                </div>
                <div className="p-2 m-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Distance
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                    disabled={true}
                    defaultValue={originDistance}
                  />
                </div>
                <div className="p-2 m-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Type
                  </label>
                  {/* <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                    value={selectedVehicle?.originVehicle}
                    onChange={(e) =>
                      setSelectedVehicle((prev) => ({
                        ...prev,
                        originVehicle: e.target.value,
                      }))
                    }
                  >
                    <option value={originvehicle[0]?._id}>
                      {originvehicle[0]?.name || "Vehicle Type"}
                    </option>
                    {originvehicle?.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.name}
                      </option>
                    ))}
                  </select> */}
                  <ReactSelect
                    styles={reactSelectStyles}
                    placeholder="Vehicle Type"
                    value={
                      originvehicle
                        .map((v) => ({ value: v._id, label: v.name }))
                        .find((o) => o.value === selectedVehicle?.originVehicle) || null
                    }
                    onChange={(option) =>
                      setSelectedVehicle((prev) => ({
                        ...prev,
                        originVehicle: option?.value,
                      }))
                    }
                    options={originvehicle.map((item) => ({
                      value: item._id,
                      label: item.name,
                    }))}
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pr-12">
              <button
                type="button"
                onClick={handlemodalclose}
                className="px-4 py-2 border rounded bg-black text-white hover:bg-yellow-600"
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 border rounded bg-yellow-600 text-white hover:bg-black"
                onClick={handleproceed}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
      <VerifyEmailModal
        open={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        defaultEmail={userDetails?.email || userDetails?.unverified_email}
        userId={userDetails?.id}
      />
      <ToastContainer position="top-center" autoClose={6000} />
    </>
  );
});

export default NewsearchForm;
